/**
 * Policy Pack Viewer & Editor - Comprehensive UI for policy pack configuration
 * Supports full editing of all policy pack sections with validation and help text
 */

import React, { useState, useCallback, useMemo } from 'react';
import { Badge } from '../UI/Badge';
import { Input } from '../UI/Input';
import { Select, SelectOption } from '../UI/Select';
import { Button } from '../UI/Button';
import { Card } from '../UI/Card';
import { 
  Shield, 
  AlertTriangle, 
  CheckCircle, 
  XCircle,
  ChevronDown,
  ChevronRight,
  FileText,
  Clock,
  AlertCircle,
  Database,
  Zap,
  Building2,
  Users,
  MapPin,
  Award,
  Target,
  Leaf,
  Settings,
  Search,
  Save,
  X,
  Plus,
  Trash2,
  Edit2,
  HelpCircle,
  Info,
  Globe,
  Code,
  List,
  Filter,
  Eye,
  EyeOff,
  Calendar,
  BarChart3,
} from 'lucide-react';

interface PolicyPackViewerProps {
  policyPack: {
    id: string;
    name: string;
    version: string;
    description: string;
    rules?: any;
    policy_data?: any;
    disclosure_text?: string;
  };
  editable?: boolean;
  onEdit?: (updatedRules: any) => void;
  onSave?: (updatedPolicyPack: any) => Promise<void>;
}

interface FormSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  color: string;
  description?: string;
}

export function PolicyPackViewer({ policyPack, editable = false, onEdit, onSave }: PolicyPackViewerProps) {
  const [activeTab, setActiveTab] = useState<string>('basic');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['basic']));
  const [searchQuery, setSearchQuery] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  
  // Get rules data - handle multiple possible locations
  const rules = policyPack.rules || policyPack.policy_data || {};
  const [formData, setFormData] = useState(() => {
    // Initialize with rules, but also merge in top-level fields if they exist
    const initialData = { ...rules };
    // If name, version, description are at top level, include them
    if (policyPack.name && !initialData.name) initialData.name = policyPack.name;
    if (policyPack.version && !initialData.version) initialData.version = policyPack.version;
    if (policyPack.description && !initialData.description) initialData.description = policyPack.description;
    
    // Debug logging
    console.log('[PolicyPackViewer] Initializing formData:', {
      rules: policyPack.rules,
      policy_data: policyPack.policy_data,
      initialData,
      data_source_configurations: initialData.data_source_configurations,
    });
    
    return initialData;
  });

  // Update form data when policy pack changes
  React.useEffect(() => {
    const newRules = policyPack.rules || policyPack.policy_data || {};
    const newData = { ...newRules };
    // Merge top-level fields
    if (policyPack.name && !newData.name) newData.name = policyPack.name;
    if (policyPack.version && !newData.version) newData.version = policyPack.version;
    if (policyPack.description && !newData.description) newData.description = policyPack.description;
    
    // Debug logging
    console.log('[PolicyPackViewer] Updating formData:', {
      newRules,
      newData,
      data_source_configurations: newData.data_source_configurations,
      keywords: newData.data_source_configurations?.keywords,
    });
    
    setFormData(newData);
    setHasChanges(false);
  }, [policyPack.rules, policyPack.policy_data, policyPack.name, policyPack.version, policyPack.description]);

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const expandAllInTab = () => {
    const activeTabGroup = tabGroups[activeTab as keyof typeof tabGroups];
    if (activeTabGroup) {
      const newExpanded = new Set(expandedSections);
      activeTabGroup.sections.forEach(sectionId => {
        newExpanded.add(sectionId);
      });
      setExpandedSections(newExpanded);
    }
  };

  const collapseAllInTab = () => {
    const activeTabGroup = tabGroups[activeTab as keyof typeof tabGroups];
    if (activeTabGroup) {
      const newExpanded = new Set(expandedSections);
      activeTabGroup.sections.forEach(sectionId => {
        newExpanded.delete(sectionId);
      });
      setExpandedSections(newExpanded);
    }
  };

  // Tab groups - organize sections into logical tabs
  const tabGroups = useMemo(() => {
    return {
      basic: {
        id: 'basic',
        label: 'Basic Info',
        icon: <Shield className="h-4 w-4" />,
        sections: ['basic'],
      },
      organization: {
        id: 'organization',
        label: 'Organization',
        icon: <Building2 className="h-4 w-4" />,
        sections: ['project_metadata', 'corporate_overview', 'key_personnel'],
      },
      reports: {
        id: 'reports',
        label: 'Reports & Templates',
        icon: <FileText className="h-4 w-4" />,
        sections: ['report_types', 'report_templates'],
      },
      evidence: {
        id: 'evidence',
        label: 'Evidence & Compliance',
        icon: <Database className="h-4 w-4" />,
        sections: ['evidence_requirements', 'compliance_rules', 'required_validations', 'compliance'],
      },
      content: {
        id: 'content',
        label: 'Content Rules',
        icon: <AlertTriangle className="h-4 w-4" />,
        sections: ['prohibited_content', 'disclosure', 'word_optimization'],
      },
      data: {
        id: 'data',
        label: 'Data Sources',
        icon: <Globe className="h-4 w-4" />,
        sections: ['data_source_configurations', 'data_source_classification', 'entity_extraction', 'entity_governance'],
      },
      ai: {
        id: 'ai',
        label: 'AI & Queries',
        icon: <Code className="h-4 w-4" />,
        sections: ['query_samples', 'conversational_context'],
      },
      export: {
        id: 'export',
        label: 'Export & Settings',
        icon: <Settings className="h-4 w-4" />,
        sections: ['export_configuration'],
      },
    };
  }, []);

  const updateField = useCallback((path: string[], value: any) => {
    setFormData((prev: any) => {
      const newData = { ...prev };
      let current = newData;
      
      for (let i = 0; i < path.length - 1; i++) {
        if (!current[path[i]]) {
          current[path[i]] = {};
        }
        current = current[path[i]];
      }
      
      current[path[path.length - 1]] = value;
      setHasChanges(true);
      return newData;
    });
  }, []);

  const updateArrayField = useCallback((path: string[], index: number, value: any) => {
    setFormData((prev: any) => {
      const newData = { ...prev };
      let current = newData;
      
      for (let i = 0; i < path.length - 1; i++) {
        if (!current[path[i]]) {
          current[path[i]] = {};
        }
        current = current[path[i]];
      }
      
      const array = [...(current[path[path.length - 1]] || [])];
      array[index] = value;
      current[path[path.length - 1]] = array;
      setHasChanges(true);
      return newData;
    });
  }, []);

  const addArrayItem = useCallback((path: string[], defaultValue: any) => {
    setFormData((prev: any) => {
      const newData = { ...prev };
      let current = newData;
      
      for (let i = 0; i < path.length - 1; i++) {
        if (!current[path[i]]) {
          current[path[i]] = {};
        }
        current = current[path[i]];
      }
      
      const array = [...(current[path[path.length - 1]] || [])];
      array.push(defaultValue);
      current[path[path.length - 1]] = array;
      setHasChanges(true);
      return newData;
    });
  }, []);

  const removeArrayItem = useCallback((path: string[], index: number) => {
    setFormData((prev: any) => {
      const newData = { ...prev };
      let current = newData;
      
      for (let i = 0; i < path.length - 1; i++) {
        if (!current[path[i]]) {
          current[path[i]] = {};
        }
        current = current[path[i]];
      }
      
      const array = [...(current[path[path.length - 1]] || [])];
      array.splice(index, 1);
      current[path[path.length - 1]] = array;
      setHasChanges(true);
      return newData;
    });
  }, []);

  const handleSave = async () => {
    if (!onSave) return;
    
    setIsSaving(true);
    try {
      await onSave({
        ...policyPack,
        rules: formData,
        policy_data: formData,
      });
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to save policy pack:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData(rules);
    setHasChanges(false);
    setValidationErrors({});
  };

  const validateField = (path: string[], value: any, required: boolean = false) => {
    const key = path.join('.');
    if (required && (!value || (typeof value === 'string' && value.trim() === ''))) {
      setValidationErrors(prev => ({ ...prev, [key]: 'This field is required' }));
      return false;
    }
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[key];
      return newErrors;
    });
    return true;
  };

  // Known sections with specialized editors
  const knownSections: FormSection[] = [
    { id: 'basic', title: 'Basic Information', icon: <Shield className="h-5 w-5" />, color: 'blue' },
    { id: 'project_metadata', title: 'Project Metadata', icon: <FileText className="h-5 w-5" />, color: 'indigo' },
    { id: 'corporate_overview', title: 'Corporate Overview', icon: <Building2 className="h-5 w-5" />, color: 'amber' },
    { id: 'key_personnel', title: 'Key Personnel', icon: <Users className="h-5 w-5" />, color: 'amber' },
    { id: 'report_types', title: 'Report Types', icon: <FileText className="h-5 w-5" />, color: 'purple' },
    { id: 'evidence_requirements', title: 'Evidence Requirements', icon: <Database className="h-5 w-5" />, color: 'green' },
    { id: 'compliance_rules', title: 'Compliance Rules', icon: <CheckCircle className="h-5 w-5" />, color: 'purple' },
    { id: 'entity_extraction', title: 'Entity Extraction', icon: <Database className="h-5 w-5" />, color: 'teal' },
    { id: 'query_samples', title: 'Query Samples', icon: <Search className="h-5 w-5" />, color: 'cyan' },
    { id: 'word_optimization', title: 'Word Optimization', icon: <Zap className="h-5 w-5" />, color: 'indigo' },
    { id: 'disclosure', title: 'Disclosure', icon: <AlertTriangle className="h-5 w-5" />, color: 'amber' },
    { id: 'prohibited_content', title: 'Prohibited Content', icon: <XCircle className="h-5 w-5" />, color: 'red' },
    { id: 'required_validations', title: 'Required Validations', icon: <CheckCircle className="h-5 w-5" />, color: 'blue' },
    { id: 'report_templates', title: 'Report Templates', icon: <FileText className="h-5 w-5" />, color: 'violet' },
    { id: 'conversational_context', title: 'Conversational Context', icon: <Code className="h-5 w-5" />, color: 'slate' },
    { id: 'export_configuration', title: 'Export Configuration', icon: <Settings className="h-5 w-5" />, color: 'gray' },
    { id: 'data_source_configurations', title: 'Data Source Configurations', icon: <Globe className="h-5 w-5" />, color: 'emerald' },
    { id: 'data_source_classification', title: 'Data Source Classification', icon: <Filter className="h-5 w-5" />, color: 'slate' },
    { id: 'entity_governance', title: 'Entity Governance', icon: <Shield className="h-5 w-5" />, color: 'indigo' },
    { id: 'compliance', title: 'Compliance', icon: <CheckCircle className="h-5 w-5" />, color: 'green' },
  ];

  // Dynamically discover additional sections from the policy pack data
  const discoverAdditionalSections = useMemo(() => {
    const knownIds = new Set(knownSections.map(s => s.id));
    const additional: FormSection[] = [];
    const colors = ['blue', 'indigo', 'purple', 'green', 'teal', 'cyan', 'amber', 'red', 'violet', 'slate', 'gray', 'emerald'];
    let colorIndex = 0;

    // Check top-level keys in formData
    Object.keys(formData).forEach((key) => {
      if (!knownIds.has(key) && formData[key] !== null && typeof formData[key] === 'object' && !Array.isArray(formData[key])) {
        const value = formData[key];
        // Only show if it's a meaningful object (not empty, has some structure)
        if (Object.keys(value).length > 0) {
          additional.push({
            id: key,
            title: key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
            icon: <Settings className="h-5 w-5" />,
            color: colors[colorIndex % colors.length],
          });
          colorIndex++;
        }
      }
    });

    return additional;
  }, [formData]);

  const sections = [...knownSections, ...discoverAdditionalSections];

  const filteredSections = useMemo(() => {
    if (!searchQuery) return sections;
    const query = searchQuery.toLowerCase();
    return sections.filter(s => 
      s.title.toLowerCase().includes(query) ||
      s.description?.toLowerCase().includes(query)
    );
  }, [searchQuery, sections]);

  return (
    <div className="space-y-4">
      {/* Header with Actions */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 p-6 rounded-lg border border-blue-200 dark:border-blue-800">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3 className="font-bold text-blue-900 dark:text-blue-100 text-xl mb-1">{policyPack.name}</h3>
            <p className="text-sm text-blue-700 dark:text-blue-300">{policyPack.description}</p>
            <div className="flex gap-2 mt-2">
              <Badge variant="success">v{policyPack.version}</Badge>
              {hasChanges && <Badge variant="default" className="bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">Unsaved Changes</Badge>}
            </div>
          </div>
          {editable && (
            <div className="flex gap-2">
              {hasChanges && (
                <>
                  <Button variant="outline" size="sm" onClick={handleCancel} disabled={isSaving}>
                    <X className="h-4 w-4 mr-1" />
                    Cancel
                  </Button>
                  <Button variant="primary" size="sm" onClick={handleSave} isLoading={isSaving}>
                    <Save className="h-4 w-4 mr-1" />
                    Save Changes
                  </Button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Search and Controls */}
        <div className="flex gap-2 items-center">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search settings..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button variant="ghost" size="sm" onClick={expandAllInTab}>
            Expand All
          </Button>
          <Button variant="ghost" size="sm" onClick={collapseAllInTab}>
            Collapse All
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <div className="flex gap-1 overflow-x-auto">
          {Object.values(tabGroups).map((tab) => {
            const tabSections = sections.filter(s => tab.sections.includes(s.id));
            if (tabSections.length === 0) return null;
            
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap
                  ${activeTab === tab.id
                    ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
                  }
                `}
              >
                {tab.icon}
                {tab.label}
                <Badge variant="default" className="ml-1 text-xs">
                  {tabSections.length}
                </Badge>
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {Object.values(tabGroups).map((tab) => {
          if (activeTab !== tab.id) return null;
          
          const tabSections = sections.filter(s => tab.sections.includes(s.id));
          const filteredTabSections = searchQuery
            ? tabSections.filter(s => 
                s.title.toLowerCase().includes(searchQuery.toLowerCase())
              )
            : tabSections;
          
          return (
            <div key={tab.id} className="space-y-4">
              {filteredTabSections.map((section) => (
                <FormSection
                  key={section.id}
                  section={section}
                  isExpanded={expandedSections.has(section.id)}
                  onToggle={() => toggleSection(section.id)}
                  formData={formData}
                  updateField={updateField}
                  updateArrayField={updateArrayField}
                  addArrayItem={addArrayItem}
                  removeArrayItem={removeArrayItem}
                  validateField={validateField}
                  validationErrors={validationErrors}
                  editable={editable}
                />
              ))}
              {filteredTabSections.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <p>No sections found matching "{searchQuery}"</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Helper component for form sections
interface FormSectionProps {
  section: FormSection;
  isExpanded: boolean;
  onToggle: () => void;
  formData: any;
  updateField: (path: string[], value: any) => void;
  updateArrayField: (path: string[], index: number, value: any) => void;
  addArrayItem: (path: string[], defaultValue: any) => void;
  removeArrayItem: (path: string[], index: number) => void;
  validateField: (path: string[], value: any, required?: boolean) => boolean;
  validationErrors: Record<string, string>;
  editable: boolean;
}

function FormSection({
  section,
  isExpanded,
  onToggle,
  formData,
  updateField,
  updateArrayField,
  addArrayItem,
  removeArrayItem,
  validateField,
  validationErrors,
  editable,
}: FormSectionProps) {
  const colorClasses: Record<string, string> = {
    blue: 'bg-blue-50 hover:bg-blue-100 border-blue-200 text-blue-900 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-100',
    indigo: 'bg-indigo-50 hover:bg-indigo-100 border-indigo-200 text-indigo-900 dark:bg-indigo-900/20 dark:border-indigo-800 dark:text-indigo-100',
    amber: 'bg-amber-50 hover:bg-amber-100 border-amber-200 text-amber-900 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-100',
    purple: 'bg-purple-50 hover:bg-purple-100 border-purple-200 text-purple-900 dark:bg-purple-900/20 dark:border-purple-800 dark:text-purple-100',
    green: 'bg-green-50 hover:bg-green-100 border-green-200 text-green-900 dark:bg-green-900/20 dark:border-green-800 dark:text-green-100',
    teal: 'bg-teal-50 hover:bg-teal-100 border-teal-200 text-teal-900 dark:bg-teal-900/20 dark:border-teal-800 dark:text-teal-100',
    cyan: 'bg-cyan-50 hover:bg-cyan-100 border-cyan-200 text-cyan-900 dark:bg-cyan-900/20 dark:border-cyan-800 dark:text-cyan-100',
    red: 'bg-red-50 hover:bg-red-100 border-red-200 text-red-900 dark:bg-red-900/20 dark:border-red-800 dark:text-red-100',
    violet: 'bg-violet-50 hover:bg-violet-100 border-violet-200 text-violet-900 dark:bg-violet-900/20 dark:border-violet-800 dark:text-violet-100',
    slate: 'bg-slate-50 hover:bg-slate-100 border-slate-200 text-slate-900 dark:bg-slate-900/20 dark:border-slate-800 dark:text-slate-100',
    gray: 'bg-gray-50 hover:bg-gray-100 border-gray-200 text-gray-900 dark:bg-gray-900/20 dark:border-gray-800 dark:text-gray-100',
    emerald: 'bg-emerald-50 hover:bg-emerald-100 border-emerald-200 text-emerald-900 dark:bg-emerald-900/20 dark:border-emerald-800 dark:text-emerald-100',
  };

  const renderSectionContent = () => {
    switch (section.id) {
      case 'basic':
        return <BasicInfoSection formData={formData} updateField={updateField} validateField={validateField} validationErrors={validationErrors} editable={editable} />;
      case 'project_metadata':
        return <ProjectMetadataSection formData={formData} updateField={updateField} updateArrayField={updateArrayField} addArrayItem={addArrayItem} removeArrayItem={removeArrayItem} validateField={validateField} validationErrors={validationErrors} editable={editable} />;
      case 'corporate_overview':
        return <CorporateOverviewSection formData={formData} updateField={updateField} updateArrayField={updateArrayField} addArrayItem={addArrayItem} removeArrayItem={removeArrayItem} validateField={validateField} validationErrors={validationErrors} editable={editable} />;
      case 'key_personnel':
        return <KeyPersonnelSection formData={formData} updateField={updateField} updateArrayField={updateArrayField} addArrayItem={addArrayItem} removeArrayItem={removeArrayItem} validateField={validateField} validationErrors={validationErrors} editable={editable} />;
      case 'report_types':
        return <ReportTypesSection formData={formData} updateField={updateField} updateArrayField={updateArrayField} addArrayItem={addArrayItem} removeArrayItem={removeArrayItem} validateField={validateField} validationErrors={validationErrors} editable={editable} />;
      case 'evidence_requirements':
        return <EvidenceRequirementsSection formData={formData} updateField={updateField} updateArrayField={updateArrayField} addArrayItem={addArrayItem} removeArrayItem={removeArrayItem} validateField={validateField} validationErrors={validationErrors} editable={editable} />;
      case 'compliance_rules':
        return <ComplianceRulesSection formData={formData} updateField={updateField} updateArrayField={updateArrayField} addArrayItem={addArrayItem} removeArrayItem={removeArrayItem} validateField={validateField} validationErrors={validationErrors} editable={editable} />;
      case 'entity_extraction':
        return <EntityExtractionSection formData={formData} updateField={updateField} validateField={validateField} validationErrors={validationErrors} editable={editable} />;
      case 'query_samples':
        return <QuerySamplesSection formData={formData} updateField={updateField} updateArrayField={updateArrayField} addArrayItem={addArrayItem} removeArrayItem={removeArrayItem} validateField={validateField} validationErrors={validationErrors} editable={editable} />;
      case 'word_optimization':
        return <WordOptimizationSection formData={formData} updateField={updateField} updateArrayField={updateArrayField} addArrayItem={addArrayItem} removeArrayItem={removeArrayItem} validateField={validateField} validationErrors={validationErrors} editable={editable} />;
      case 'disclosure':
        return <DisclosureSection formData={formData} updateField={updateField} validateField={validateField} validationErrors={validationErrors} editable={editable} />;
      case 'prohibited_content':
        return <ProhibitedContentSection formData={formData} updateField={updateField} updateArrayField={updateArrayField} addArrayItem={addArrayItem} removeArrayItem={removeArrayItem} validateField={validateField} validationErrors={validationErrors} editable={editable} />;
      case 'required_validations':
        return <RequiredValidationsSection formData={formData} updateField={updateField} updateArrayField={updateArrayField} addArrayItem={addArrayItem} removeArrayItem={removeArrayItem} validateField={validateField} validationErrors={validationErrors} editable={editable} />;
      case 'report_templates':
        return <ReportTemplatesSection formData={formData} updateField={updateField} validateField={validateField} validationErrors={validationErrors} editable={editable} />;
      case 'conversational_context':
        return <ConversationalContextSection formData={formData} updateField={updateField} validateField={validateField} validationErrors={validationErrors} editable={editable} />;
      case 'export_configuration':
        return <ExportConfigurationSection formData={formData} updateField={updateField} updateArrayField={updateArrayField} addArrayItem={addArrayItem} removeArrayItem={removeArrayItem} validateField={validateField} validationErrors={validationErrors} editable={editable} />;
      case 'data_source_configurations':
        return <DataSourceConfigurationsSection formData={formData} updateField={updateField} updateArrayField={updateArrayField} addArrayItem={addArrayItem} removeArrayItem={removeArrayItem} validateField={validateField} validationErrors={validationErrors} editable={editable} />;
      case 'data_source_classification':
        return <DataSourceClassificationSection formData={formData} updateField={updateField} validateField={validateField} validationErrors={validationErrors} editable={editable} />;
      case 'entity_governance':
        return <EntityGovernanceSection formData={formData} updateField={updateField} updateArrayField={updateArrayField} addArrayItem={addArrayItem} removeArrayItem={removeArrayItem} validateField={validateField} validationErrors={validationErrors} editable={editable} />;
      case 'compliance':
        return <ComplianceSection formData={formData} updateField={updateField} updateArrayField={updateArrayField} addArrayItem={addArrayItem} removeArrayItem={removeArrayItem} validateField={validateField} validationErrors={validationErrors} editable={editable} />;
      default:
        // Graceful degradation: Use generic editor for unknown sections
        return <GenericSectionEditor 
          sectionId={section.id} 
          sectionData={formData[section.id]} 
          formData={formData}
          updateField={updateField}
          updateArrayField={updateArrayField}
          addArrayItem={addArrayItem}
          removeArrayItem={removeArrayItem}
          validateField={validateField}
          validationErrors={validationErrors}
          editable={editable}
        />;
    }
  };

  return (
    <Card className="overflow-hidden">
      <button
        onClick={onToggle}
        className={`w-full ${colorClasses[section.color]} transition-colors p-4 flex items-center justify-between`}
      >
        <div className="flex items-center gap-3">
          {section.icon}
          <span className="font-semibold">{section.title}</span>
          {section.description && (
            <span className="text-sm opacity-75 ml-2">{section.description}</span>
          )}
        </div>
        {isExpanded ? (
          <ChevronDown className="h-5 w-5" />
        ) : (
          <ChevronRight className="h-5 w-5" />
        )}
      </button>
      {isExpanded && (
        <div className="p-6 bg-white dark:bg-theme-paper">
          {renderSectionContent()}
        </div>
      )}
    </Card>
  );
}

// Individual section components (to be implemented)
function BasicInfoSection({ formData, updateField, validateField, validationErrors, editable }: any) {
  // Gracefully handle missing fields - use policyPack data as fallback
  const name = formData.name || '';
  const version = formData.version || '';
  const description = formData.description || '';
  
  return (
    <div className="space-y-4">
      <Input
        label="Name"
        value={name}
        onChange={(e) => {
          updateField(['name'], e.target.value);
          validateField(['name'], e.target.value, true);
        }}
        error={validationErrors['name']}
        helperText="The display name for this policy pack"
        disabled={!editable}
        required
      />
      <Input
        label="Version"
        value={version}
        onChange={(e) => {
          updateField(['version'], e.target.value);
          validateField(['version'], e.target.value, true);
        }}
        error={validationErrors['version']}
        helperText="Version number (e.g., 2.0.0)"
        disabled={!editable}
        required
      />
      <div>
        <label className="block text-sm font-medium text-theme-text mb-1">
          Description
        </label>
        <textarea
          value={description}
          onChange={(e) => {
            updateField(['description'], e.target.value);
            validateField(['description'], e.target.value, true);
          }}
          className="w-full rounded-lg border border-theme-border px-3 py-2 bg-theme-paper text-theme-text focus:outline-none focus:ring-2 focus:ring-primary-500"
          rows={3}
          disabled={!editable}
        />
        {validationErrors['description'] && (
          <p className="mt-1 text-sm text-error-dark">{validationErrors['description']}</p>
        )}
        <p className="mt-1 text-sm text-theme-text-secondary">Detailed description of the policy pack</p>
      </div>
    </div>
  );
}

// Placeholder for other sections - will implement all of them
function ProjectMetadataSection({ formData, updateField, updateArrayField, addArrayItem, removeArrayItem, validateField, validationErrors, editable }: any) {
  const serviceLines = formData.project_metadata?.service_lines || [];
  
  return (
    <div className="space-y-6">
      <Input
        label="Organization Name"
        value={formData.project_metadata?.organization_name || ''}
        onChange={(e) => updateField(['project_metadata', 'organization_name'], e.target.value)}
        helperText="The organization this policy pack is configured for"
        disabled={!editable}
      />
      
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-theme-text">Service Lines</label>
          {editable && (
            <Button variant="ghost" size="sm" onClick={() => addArrayItem(['project_metadata', 'service_lines'], { name: '', description: '', key_capabilities: [], key_differentiators: [] })}>
              <Plus className="h-4 w-4 mr-1" />
              Add Service Line
            </Button>
          )}
        </div>
        <div className="space-y-4">
          {serviceLines.map((sl: any, idx: number) => (
            <Card key={idx} className="p-4">
              <div className="flex items-start justify-between mb-3">
                <h4 className="font-semibold">Service Line {idx + 1}</h4>
                {editable && (
                  <Button variant="ghost" size="sm" onClick={() => removeArrayItem(['project_metadata', 'service_lines'], idx)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <div className="space-y-3">
                <Input
                  label="Name"
                  value={sl.name || ''}
                  onChange={(e) => updateArrayField(['project_metadata', 'service_lines'], idx, { ...sl, name: e.target.value })}
                  disabled={!editable}
                />
                <div>
                  <label className="block text-sm font-medium text-theme-text mb-1">Description</label>
                  <textarea
                    value={sl.description || ''}
                    onChange={(e) => updateArrayField(['project_metadata', 'service_lines'], idx, { ...sl, description: e.target.value })}
                    className="w-full rounded-lg border border-theme-border px-3 py-2 bg-theme-paper text-theme-text focus:outline-none focus:ring-2 focus:ring-primary-500"
                    rows={3}
                    disabled={!editable}
                  />
                </div>
                <ArrayFieldEditor
                  label="Key Capabilities"
                  items={sl.key_capabilities || []}
                  onAdd={() => {
                    const newItems = [...(sl.key_capabilities || []), ''];
                    updateArrayField(['project_metadata', 'service_lines'], idx, { ...sl, key_capabilities: newItems });
                  }}
                  onRemove={(itemIdx) => {
                    const newItems = [...(sl.key_capabilities || [])];
                    newItems.splice(itemIdx, 1);
                    updateArrayField(['project_metadata', 'service_lines'], idx, { ...sl, key_capabilities: newItems });
                  }}
                  onUpdate={(itemIdx, value) => {
                    const newItems = [...(sl.key_capabilities || [])];
                    newItems[itemIdx] = value;
                    updateArrayField(['project_metadata', 'service_lines'], idx, { ...sl, key_capabilities: newItems });
                  }}
                  editable={editable}
                />
                <ArrayFieldEditor
                  label="Key Differentiators"
                  items={sl.key_differentiators || []}
                  onAdd={() => {
                    const newItems = [...(sl.key_differentiators || []), ''];
                    updateArrayField(['project_metadata', 'service_lines'], idx, { ...sl, key_differentiators: newItems });
                  }}
                  onRemove={(itemIdx) => {
                    const newItems = [...(sl.key_differentiators || [])];
                    newItems.splice(itemIdx, 1);
                    updateArrayField(['project_metadata', 'service_lines'], idx, { ...sl, key_differentiators: newItems });
                  }}
                  onUpdate={(itemIdx, value) => {
                    const newItems = [...(sl.key_differentiators || [])];
                    newItems[itemIdx] = value;
                    updateArrayField(['project_metadata', 'service_lines'], idx, { ...sl, key_differentiators: newItems });
                  }}
                  editable={editable}
                />
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Regulatory Frameworks */}
      <div>
        <label className="block text-sm font-medium text-theme-text mb-2">Regulatory Frameworks</label>
        <ArrayFieldEditor
          label=""
          items={formData.project_metadata?.regulatory_frameworks || []}
          onAdd={() => {
            const items = [...(formData.project_metadata?.regulatory_frameworks || []), ''];
            updateField(['project_metadata', 'regulatory_frameworks'], items);
          }}
          onRemove={(idx) => {
            const items = [...(formData.project_metadata?.regulatory_frameworks || [])];
            items.splice(idx, 1);
            updateField(['project_metadata', 'regulatory_frameworks'], items);
          }}
          onUpdate={(idx, value) => {
            const items = [...(formData.project_metadata?.regulatory_frameworks || [])];
            items[idx] = value;
            updateField(['project_metadata', 'regulatory_frameworks'], items);
          }}
          editable={editable}
        />
      </div>

      {/* Professional Designations */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold">Professional Designations</h4>
          {editable && (
            <Button variant="ghost" size="sm" onClick={() => {
              const items = [...(formData.project_metadata?.professional_designations || []), {
                code: '',
                name: '',
                regulatory_body: '',
                tier: 1,
                count: 0,
                required_for: []
              }];
              updateField(['project_metadata', 'professional_designations'], items);
            }}>
              <Plus className="h-4 w-4 mr-1" />
              Add Designation
            </Button>
          )}
        </div>
        <div className="space-y-4">
          {(formData.project_metadata?.professional_designations || []).map((pd: any, idx: number) => (
            <Card key={idx} className="p-3">
              <div className="flex items-start justify-between mb-3">
                <h5 className="font-medium">{pd.code || `Designation ${idx + 1}`}</h5>
                {editable && (
                  <Button variant="ghost" size="sm" onClick={() => {
                    const items = [...(formData.project_metadata?.professional_designations || [])];
                    items.splice(idx, 1);
                    updateField(['project_metadata', 'professional_designations'], items);
                  }}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Code"
                  value={pd.code || ''}
                  onChange={(e) => {
                    const items = [...(formData.project_metadata?.professional_designations || [])];
                    items[idx] = { ...pd, code: e.target.value };
                    updateField(['project_metadata', 'professional_designations'], items);
                  }}
                  disabled={!editable}
                />
                <Input
                  label="Name"
                  value={pd.name || ''}
                  onChange={(e) => {
                    const items = [...(formData.project_metadata?.professional_designations || [])];
                    items[idx] = { ...pd, name: e.target.value };
                    updateField(['project_metadata', 'professional_designations'], items);
                  }}
                  disabled={!editable}
                />
                <Input
                  label="Regulatory Body"
                  value={pd.regulatory_body || ''}
                  onChange={(e) => {
                    const items = [...(formData.project_metadata?.professional_designations || [])];
                    items[idx] = { ...pd, regulatory_body: e.target.value };
                    updateField(['project_metadata', 'professional_designations'], items);
                  }}
                  disabled={!editable}
                />
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    label="Tier"
                    type="number"
                    value={pd.tier || 1}
                    onChange={(e) => {
                      const items = [...(formData.project_metadata?.professional_designations || [])];
                      items[idx] = { ...pd, tier: parseInt(e.target.value) || 1 };
                      updateField(['project_metadata', 'professional_designations'], items);
                    }}
                    disabled={!editable}
                  />
                  <Input
                    label="Count"
                    type="number"
                    value={pd.count || 0}
                    onChange={(e) => {
                      const items = [...(formData.project_metadata?.professional_designations || [])];
                      items[idx] = { ...pd, count: parseInt(e.target.value) || 0 };
                      updateField(['project_metadata', 'professional_designations'], items);
                    }}
                    disabled={!editable}
                  />
                </div>
                <ArrayFieldEditor
                  label="Required For"
                  items={pd.required_for || []}
                  onAdd={() => {
                    const items = [...(formData.project_metadata?.professional_designations || [])];
                    const requiredFor = [...(pd.required_for || []), ''];
                    items[idx] = { ...pd, required_for: requiredFor };
                    updateField(['project_metadata', 'professional_designations'], items);
                  }}
                  onRemove={(itemIdx) => {
                    const items = [...(formData.project_metadata?.professional_designations || [])];
                    const requiredFor = [...(pd.required_for || [])];
                    requiredFor.splice(itemIdx, 1);
                    items[idx] = { ...pd, required_for: requiredFor };
                    updateField(['project_metadata', 'professional_designations'], items);
                  }}
                  onUpdate={(itemIdx, value) => {
                    const items = [...(formData.project_metadata?.professional_designations || [])];
                    const requiredFor = [...(pd.required_for || [])];
                    requiredFor[itemIdx] = value;
                    items[idx] = { ...pd, required_for: requiredFor };
                    updateField(['project_metadata', 'professional_designations'], items);
                  }}
                  editable={editable}
                />
              </div>
            </Card>
          ))}
        </div>
      </Card>

      {/* Proposal Volume */}
      <Card className="p-4">
        <h4 className="font-semibold mb-4">Proposal Volume</h4>
        <div className="grid grid-cols-3 gap-4">
          <Input
            label="Annual RFPs"
            type="number"
            value={formData.project_metadata?.proposal_volume?.annual_rfps || ''}
            onChange={(e) => updateField(['project_metadata', 'proposal_volume', 'annual_rfps'], parseInt(e.target.value) || 0)}
            disabled={!editable}
          />
          <Input
            label="Historical Proposals"
            type="number"
            value={formData.project_metadata?.proposal_volume?.historical_proposals || ''}
            onChange={(e) => updateField(['project_metadata', 'proposal_volume', 'historical_proposals'], parseInt(e.target.value) || 0)}
            disabled={!editable}
          />
          <Input
            label="SOP Documents"
            type="number"
            value={formData.project_metadata?.proposal_volume?.sop_documents || ''}
            onChange={(e) => updateField(['project_metadata', 'proposal_volume', 'sop_documents'], parseInt(e.target.value) || 0)}
            disabled={!editable}
          />
        </div>
      </Card>

      {/* Data Sources */}
      <div>
        <label className="block text-sm font-medium text-theme-text mb-2">Data Sources</label>
        <ArrayFieldEditor
          label=""
          items={formData.project_metadata?.data_sources || []}
          onAdd={() => {
            const items = [...(formData.project_metadata?.data_sources || []), ''];
            updateField(['project_metadata', 'data_sources'], items);
          }}
          onRemove={(idx) => {
            const items = [...(formData.project_metadata?.data_sources || [])];
            items.splice(idx, 1);
            updateField(['project_metadata', 'data_sources'], items);
          }}
          onUpdate={(idx, value) => {
            const items = [...(formData.project_metadata?.data_sources || [])];
            items[idx] = value;
            updateField(['project_metadata', 'data_sources'], items);
          }}
          editable={editable}
        />
      </div>

      {/* Organized Folder Structure */}
      {formData.project_metadata?.organized_folder_structure && (
        <Card className="p-4">
          <h4 className="font-semibold mb-4">Organized Folder Structure</h4>
          <div className="space-y-3">
            <Input
              label="File Path"
              value={formData.project_metadata.organized_folder_structure.file || ''}
              onChange={(e) => updateField(['project_metadata', 'organized_folder_structure', 'file'], e.target.value)}
              disabled={!editable}
            />
            <Input
              label="Version"
              value={formData.project_metadata.organized_folder_structure.version || ''}
              onChange={(e) => updateField(['project_metadata', 'organized_folder_structure', 'version'], e.target.value)}
              disabled={!editable}
            />
            <div>
              <label className="block text-sm font-medium text-theme-text mb-1">Description</label>
              <textarea
                value={formData.project_metadata.organized_folder_structure.description || ''}
                onChange={(e) => updateField(['project_metadata', 'organized_folder_structure', 'description'], e.target.value)}
                className="w-full rounded-lg border border-theme-border px-3 py-2 bg-theme-paper text-theme-text focus:outline-none focus:ring-2 focus:ring-primary-500"
                rows={2}
                disabled={!editable}
              />
            </div>
            <ArrayFieldEditor
              label="Categories"
              items={formData.project_metadata.organized_folder_structure.categories || []}
              onAdd={() => {
                const items = [...(formData.project_metadata.organized_folder_structure.categories || []), ''];
                updateField(['project_metadata', 'organized_folder_structure', 'categories'], items);
              }}
              onRemove={(idx) => {
                const items = [...(formData.project_metadata.organized_folder_structure.categories || [])];
                items.splice(idx, 1);
                updateField(['project_metadata', 'organized_folder_structure', 'categories'], items);
              }}
              onUpdate={(idx, value) => {
                const items = [...(formData.project_metadata.organized_folder_structure.categories || [])];
                items[idx] = value;
                updateField(['project_metadata', 'organized_folder_structure', 'categories'], items);
              }}
              editable={editable}
            />
          </div>
        </Card>
      )}
    </div>
  );
}

// Helper component for editing arrays of strings
function ArrayFieldEditor({ label, items, onAdd, onRemove, onUpdate, editable }: any) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium text-theme-text">{label}</label>
        {editable && (
          <Button variant="ghost" size="sm" onClick={onAdd}>
            <Plus className="h-4 w-4 mr-1" />
            Add
          </Button>
        )}
      </div>
      <div className="space-y-2">
        {items.map((item: string, idx: number) => (
          <div key={idx} className="flex gap-2">
            <Input
              value={item}
              onChange={(e) => onUpdate(idx, e.target.value)}
              disabled={!editable}
              className="flex-1"
            />
            {editable && (
              <Button variant="ghost" size="sm" onClick={() => onRemove(idx)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        ))}
        {items.length === 0 && (
          <p className="text-sm text-theme-text-secondary italic">No items. Click "Add" to add one.</p>
        )}
      </div>
    </div>
  );
}

// Corporate Overview Section
function CorporateOverviewSection({ formData, updateField, updateArrayField, addArrayItem, removeArrayItem, validateField, validationErrors, editable }: any) {
  // Graceful degradation: handle missing corporate_overview
  const co = formData.corporate_overview || {};
  
  // If section is completely empty, show a message
  if (Object.keys(co).length === 0) {
    return (
      <div className="p-4 text-center text-theme-text-secondary">
        <p className="text-sm mb-2">No corporate overview data configured.</p>
        {editable && (
          <Button variant="ghost" size="sm" onClick={() => updateField(['corporate_overview'], {})}>
            Initialize Corporate Overview
          </Button>
        )}
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Mission */}
      <Card className="p-4">
        <h4 className="font-semibold mb-4 flex items-center gap-2">
          <Target className="h-4 w-4" />
          Mission
        </h4>
        <div className="space-y-3">
          <Input
            label="Motto"
            value={co.mission?.motto || ''}
            onChange={(e) => updateField(['corporate_overview', 'mission', 'motto'], e.target.value)}
            helperText="Company motto or tagline"
            disabled={!editable}
          />
          <div>
            <label className="block text-sm font-medium text-theme-text mb-1">Philosophy</label>
            <textarea
              value={co.mission?.philosophy || ''}
              onChange={(e) => updateField(['corporate_overview', 'mission', 'philosophy'], e.target.value)}
              className="w-full rounded-lg border border-theme-border px-3 py-2 bg-theme-paper text-theme-text focus:outline-none focus:ring-2 focus:ring-primary-500"
              rows={2}
              disabled={!editable}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-theme-text mb-1">Focus</label>
            <textarea
              value={co.mission?.focus || ''}
              onChange={(e) => updateField(['corporate_overview', 'mission', 'focus'], e.target.value)}
              className="w-full rounded-lg border border-theme-border px-3 py-2 bg-theme-paper text-theme-text focus:outline-none focus:ring-2 focus:ring-primary-500"
              rows={3}
              disabled={!editable}
            />
          </div>
        </div>
      </Card>

      {/* History */}
      <Card className="p-4">
        <h4 className="font-semibold mb-4 flex items-center gap-2">
          <Clock className="h-4 w-4" />
          History
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Established"
            type="number"
            value={co.history?.established || ''}
            onChange={(e) => updateField(['corporate_overview', 'history', 'established'], e.target.value)}
            helperText="Year company was established"
            disabled={!editable}
          />
          <Input
            label="Years of Experience"
            type="number"
            value={co.history?.years_of_experience || ''}
            onChange={(e) => updateField(['corporate_overview', 'history', 'years_of_experience'], e.target.value)}
            disabled={!editable}
          />
        </div>
        <div className="mt-3">
          <Input
            label="Size"
            value={co.history?.size || ''}
            onChange={(e) => updateField(['corporate_overview', 'history', 'size'], e.target.value)}
            helperText="Company size description (e.g., '150+ professionals')"
            disabled={!editable}
          />
        </div>
        <ArrayFieldEditor
          label="Core Services"
          items={co.history?.core_services || []}
          onAdd={() => {
            const items = [...(co.history?.core_services || []), ''];
            updateField(['corporate_overview', 'history', 'core_services'], items);
          }}
          onRemove={(idx) => {
            const items = [...(co.history?.core_services || [])];
            items.splice(idx, 1);
            updateField(['corporate_overview', 'history', 'core_services'], items);
          }}
          onUpdate={(idx, value) => {
            const items = [...(co.history?.core_services || [])];
            items[idx] = value;
            updateField(['corporate_overview', 'history', 'core_services'], items);
          }}
          editable={editable}
        />
      </Card>

      {/* Values */}
      <Card className="p-4">
        <h4 className="font-semibold mb-4 flex items-center gap-2">
          <Award className="h-4 w-4" />
          Values
        </h4>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-theme-text mb-1">Health & Safety Description</label>
            <textarea
              value={co.values?.health_safety?.description || ''}
              onChange={(e) => updateField(['corporate_overview', 'values', 'health_safety', 'description'], e.target.value)}
              className="w-full rounded-lg border border-theme-border px-3 py-2 bg-theme-paper text-theme-text focus:outline-none focus:ring-2 focus:ring-primary-500"
              rows={3}
              disabled={!editable}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-theme-text mb-1">Sustainability - Environmental</label>
            <textarea
              value={co.values?.sustainability?.environmental || ''}
              onChange={(e) => updateField(['corporate_overview', 'values', 'sustainability', 'environmental'], e.target.value)}
              className="w-full rounded-lg border border-theme-border px-3 py-2 bg-theme-paper text-theme-text focus:outline-none focus:ring-2 focus:ring-primary-500"
              rows={2}
              disabled={!editable}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-theme-text mb-1">Sustainability - Economic</label>
            <textarea
              value={co.values?.sustainability?.economic || ''}
              onChange={(e) => updateField(['corporate_overview', 'values', 'sustainability', 'economic'], e.target.value)}
              className="w-full rounded-lg border border-theme-border px-3 py-2 bg-theme-paper text-theme-text focus:outline-none focus:ring-2 focus:ring-primary-500"
              rows={2}
              disabled={!editable}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-theme-text mb-1">Sustainability - Social</label>
            <textarea
              value={co.values?.sustainability?.social || ''}
              onChange={(e) => updateField(['corporate_overview', 'values', 'sustainability', 'social'], e.target.value)}
              className="w-full rounded-lg border border-theme-border px-3 py-2 bg-theme-paper text-theme-text focus:outline-none focus:ring-2 focus:ring-primary-500"
              rows={2}
              disabled={!editable}
            />
          </div>
        </div>
      </Card>

      {/* Commitments */}
      <Card className="p-4">
        <h4 className="font-semibold mb-4 flex items-center gap-2">
          <Leaf className="h-4 w-4" />
          Commitments
        </h4>
        <div className="space-y-4">
          <div>
            <Input
              label="Net-Zero Target Year"
              type="number"
              value={co.commitments?.net_zero?.target_year || ''}
              onChange={(e) => updateField(['corporate_overview', 'commitments', 'net_zero', 'target_year'], e.target.value)}
              disabled={!editable}
            />
            <div className="mt-2">
              <label className="block text-sm font-medium text-theme-text mb-1">Net-Zero Commitment</label>
              <textarea
                value={co.commitments?.net_zero?.commitment || ''}
                onChange={(e) => updateField(['corporate_overview', 'commitments', 'net_zero', 'commitment'], e.target.value)}
                className="w-full rounded-lg border border-theme-border px-3 py-2 bg-theme-paper text-theme-text focus:outline-none focus:ring-2 focus:ring-primary-500"
                rows={2}
                disabled={!editable}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-theme-text mb-1">Indigenous Reconciliation Commitment</label>
            <textarea
              value={co.commitments?.indigenous_reconciliation?.commitment || ''}
              onChange={(e) => updateField(['corporate_overview', 'commitments', 'indigenous_reconciliation', 'commitment'], e.target.value)}
              className="w-full rounded-lg border border-theme-border px-3 py-2 bg-theme-paper text-theme-text focus:outline-none focus:ring-2 focus:ring-primary-500"
              rows={3}
              disabled={!editable}
            />
          </div>
        </div>
      </Card>

      {/* Geographic Presence */}
      <Card className="p-4">
        <h4 className="font-semibold mb-4 flex items-center gap-2">
          <MapPin className="h-4 w-4" />
          Geographic Presence
        </h4>
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-theme-text">Offices</label>
              {editable && (
                <Button variant="ghost" size="sm" onClick={() => {
                  const offices = [...(co.geographic_presence?.offices || []), { location: '', type: '' }];
                  updateField(['corporate_overview', 'geographic_presence', 'offices'], offices);
                }}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Office
                </Button>
              )}
            </div>
            <div className="space-y-2">
              {(co.geographic_presence?.offices || []).map((office: any, idx: number) => (
                <div key={idx} className="flex gap-2">
                  <Input
                    value={office.location || ''}
                    onChange={(e) => {
                      const offices = [...(co.geographic_presence?.offices || [])];
                      offices[idx] = { ...office, location: e.target.value };
                      updateField(['corporate_overview', 'geographic_presence', 'offices'], offices);
                    }}
                    placeholder="Location"
                    disabled={!editable}
                    className="flex-1"
                  />
                  <Input
                    value={office.type || ''}
                    onChange={(e) => {
                      const offices = [...(co.geographic_presence?.offices || [])];
                      offices[idx] = { ...office, type: e.target.value };
                      updateField(['corporate_overview', 'geographic_presence', 'offices'], offices);
                    }}
                    placeholder="Type (e.g., Head Office)"
                    disabled={!editable}
                    className="flex-1"
                  />
                  {editable && (
                    <Button variant="ghost" size="sm" onClick={() => {
                      const offices = [...(co.geographic_presence?.offices || [])];
                      offices.splice(idx, 1);
                      updateField(['corporate_overview', 'geographic_presence', 'offices'], offices);
                    }}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {/* Team Composition */}
      {co.team_composition && (
        <Card className="p-4">
          <h4 className="font-semibold mb-4 flex items-center gap-2">
            <Users className="h-4 w-4" />
            Team Composition
          </h4>
          <div className="space-y-3">
            <Input
              label="Total Staff"
              value={co.team_composition?.total_staff || ''}
              onChange={(e) => updateField(['corporate_overview', 'team_composition', 'total_staff'], e.target.value)}
              disabled={!editable}
            />
            {co.team_composition?.professional_designations && (
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(co.team_composition.professional_designations).map(([key, value]: [string, any]) => (
                  <Input
                    key={key}
                    label={key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                    value={String(value || '')}
                    onChange={(e) => updateField(['corporate_overview', 'team_composition', 'professional_designations', key], e.target.value)}
                    disabled={!editable}
                  />
                ))}
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Subsidiaries */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            Subsidiaries
          </h4>
          {editable && (
            <Button variant="ghost" size="sm" onClick={() => {
              const items = [...(co.subsidiaries || []), { name: '', type: '', specialization: '' }];
              updateField(['corporate_overview', 'subsidiaries'], items);
            }}>
              <Plus className="h-4 w-4 mr-1" />
              Add Subsidiary
            </Button>
          )}
        </div>
        <div className="space-y-2">
          {(co.subsidiaries || []).map((sub: any, idx: number) => (
            <div key={idx} className="flex gap-2">
              <Input
                value={sub.name || ''}
                onChange={(e) => {
                  const items = [...(co.subsidiaries || [])];
                  items[idx] = { ...sub, name: e.target.value };
                  updateField(['corporate_overview', 'subsidiaries'], items);
                }}
                placeholder="Name"
                disabled={!editable}
                className="flex-1"
              />
              <Input
                value={sub.type || ''}
                onChange={(e) => {
                  const items = [...(co.subsidiaries || [])];
                  items[idx] = { ...sub, type: e.target.value };
                  updateField(['corporate_overview', 'subsidiaries'], items);
                }}
                placeholder="Type"
                disabled={!editable}
                className="flex-1"
              />
              <Input
                value={sub.specialization || ''}
                onChange={(e) => {
                  const items = [...(co.subsidiaries || [])];
                  items[idx] = { ...sub, specialization: e.target.value };
                  updateField(['corporate_overview', 'subsidiaries'], items);
                }}
                placeholder="Specialization"
                disabled={!editable}
                className="flex-1"
              />
              {editable && (
                <Button variant="ghost" size="sm" onClick={() => {
                  const items = [...(co.subsidiaries || [])];
                  items.splice(idx, 1);
                  updateField(['corporate_overview', 'subsidiaries'], items);
                }}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
      </Card>

      {/* Certifications */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold flex items-center gap-2">
            <Award className="h-4 w-4" />
            Certifications
          </h4>
          {editable && (
            <Button variant="ghost" size="sm" onClick={() => {
              const items = [...(co.certifications || []), { name: '', description: '', count: 0 }];
              updateField(['corporate_overview', 'certifications'], items);
            }}>
              <Plus className="h-4 w-4 mr-1" />
              Add Certification
            </Button>
          )}
        </div>
        <div className="space-y-2">
          {(co.certifications || []).map((cert: any, idx: number) => (
            <div key={idx} className="flex gap-2 items-start">
              <Input
                value={cert.name || ''}
                onChange={(e) => {
                  const items = [...(co.certifications || [])];
                  items[idx] = { ...cert, name: e.target.value };
                  updateField(['corporate_overview', 'certifications'], items);
                }}
                placeholder="Name"
                disabled={!editable}
                className="flex-1"
              />
              <Input
                value={cert.description || ''}
                onChange={(e) => {
                  const items = [...(co.certifications || [])];
                  items[idx] = { ...cert, description: e.target.value };
                  updateField(['corporate_overview', 'certifications'], items);
                }}
                placeholder="Description"
                disabled={!editable}
                className="flex-1"
              />
              {cert.count !== undefined && (
                <Input
                  type="number"
                  value={cert.count || 0}
                  onChange={(e) => {
                    const items = [...(co.certifications || [])];
                    items[idx] = { ...cert, count: parseInt(e.target.value) || 0 };
                    updateField(['corporate_overview', 'certifications'], items);
                  }}
                  placeholder="Count"
                  disabled={!editable}
                  className="w-24"
                />
              )}
              {editable && (
                <Button variant="ghost" size="sm" onClick={() => {
                  const items = [...(co.certifications || [])];
                  items.splice(idx, 1);
                  updateField(['corporate_overview', 'certifications'], items);
                }}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
      </Card>

      {/* Key Differentiators */}
      <Card className="p-4">
        <h4 className="font-semibold mb-4 flex items-center gap-2">
          <Target className="h-4 w-4" />
          Key Differentiators
        </h4>
        <ArrayFieldEditor
          label=""
          items={co.key_differentiators || []}
          onAdd={() => {
            const items = [...(co.key_differentiators || []), ''];
            updateField(['corporate_overview', 'key_differentiators'], items);
          }}
          onRemove={(idx) => {
            const items = [...(co.key_differentiators || [])];
            items.splice(idx, 1);
            updateField(['corporate_overview', 'key_differentiators'], items);
          }}
          onUpdate={(idx, value) => {
            const items = [...(co.key_differentiators || [])];
            items[idx] = value;
            updateField(['corporate_overview', 'key_differentiators'], items);
          }}
          editable={editable}
        />
      </Card>

      {/* Competitive Advantages */}
      <Card className="p-4">
        <h4 className="font-semibold mb-4 flex items-center gap-2">
          <Award className="h-4 w-4" />
          Competitive Advantages
        </h4>
        <ArrayFieldEditor
          label=""
          items={co.competitive_advantages || []}
          onAdd={() => {
            const items = [...(co.competitive_advantages || []), ''];
            updateField(['corporate_overview', 'competitive_advantages'], items);
          }}
          onRemove={(idx) => {
            const items = [...(co.competitive_advantages || [])];
            items.splice(idx, 1);
            updateField(['corporate_overview', 'competitive_advantages'], items);
          }}
          onUpdate={(idx, value) => {
            const items = [...(co.competitive_advantages || [])];
            items[idx] = value;
            updateField(['corporate_overview', 'competitive_advantages'], items);
          }}
          editable={editable}
        />
      </Card>
    </div>
  );
}

// Key Personnel Section
function KeyPersonnelSection({ formData, updateField, updateArrayField, addArrayItem, removeArrayItem, validateField, validationErrors, editable }: any) {
  // Graceful degradation: handle both array and object formats
  let personnel: any[] = [];
  if (Array.isArray(formData.key_personnel)) {
    personnel = formData.key_personnel;
  } else if (formData.key_personnel && typeof formData.key_personnel === 'object') {
    personnel = Object.values(formData.key_personnel);
  }
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-theme-text-secondary">Manage key personnel and their qualifications</p>
        {editable && (
          <Button variant="ghost" size="sm" onClick={() => {
            const newPerson = {
              name: '',
              role: '',
              organization: '',
              designations: [],
              qualifications: [],
              expertise_areas: [],
              location: '',
              contact: { email: '', phone: '' }
            };
            if (Array.isArray(formData.key_personnel)) {
              addArrayItem(['key_personnel'], newPerson);
            } else {
              updateField(['key_personnel'], [...personnel, newPerson]);
            }
          }}>
            <Plus className="h-4 w-4 mr-1" />
            Add Personnel
          </Button>
        )}
      </div>
      {personnel.length === 0 && (
        <div className="p-4 text-center text-theme-text-secondary text-sm">
          No personnel configured. Click "Add Personnel" to get started.
        </div>
      )}
      {personnel.map((person: any, idx: number) => {
        // Graceful degradation: ensure person object has expected structure
        const safePerson = {
          name: person?.name || '',
          role: person?.role || '',
          organization: person?.organization || '',
          designations: Array.isArray(person?.designations) ? person.designations : [],
          qualifications: Array.isArray(person?.qualifications) ? person.qualifications : [],
          expertise_areas: Array.isArray(person?.expertise_areas) ? person.expertise_areas : [],
          location: person?.location || '',
          contact: person?.contact || { email: '', phone: '' }
        };
        return (
          <Card key={idx} className="p-4">
            <div className="flex items-start justify-between mb-4">
              <h4 className="font-semibold">Person {idx + 1}</h4>
              {editable && (
                <Button variant="ghost" size="sm" onClick={() => {
                  if (Array.isArray(formData.key_personnel)) {
                    removeArrayItem(['key_personnel'], idx);
                  } else {
                    const newPersonnel = [...personnel];
                    newPersonnel.splice(idx, 1);
                    updateField(['key_personnel'], newPersonnel);
                  }
                }}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Name"
                value={safePerson.name}
                onChange={(e) => {
                  const updated = { ...safePerson, name: e.target.value };
                  if (Array.isArray(formData.key_personnel)) {
                    updateArrayField(['key_personnel'], idx, updated);
                  } else {
                    const newPersonnel = [...personnel];
                    newPersonnel[idx] = updated;
                    updateField(['key_personnel'], newPersonnel);
                  }
                }}
                disabled={!editable}
              />
              <Input
                label="Role"
                value={safePerson.role}
                onChange={(e) => {
                  const updated = { ...safePerson, role: e.target.value };
                  if (Array.isArray(formData.key_personnel)) {
                    updateArrayField(['key_personnel'], idx, updated);
                  } else {
                    const newPersonnel = [...personnel];
                    newPersonnel[idx] = updated;
                    updateField(['key_personnel'], newPersonnel);
                  }
                }}
                disabled={!editable}
              />
              <Input
                label="Organization"
                value={safePerson.organization}
                onChange={(e) => {
                  const updated = { ...safePerson, organization: e.target.value };
                  if (Array.isArray(formData.key_personnel)) {
                    updateArrayField(['key_personnel'], idx, updated);
                  } else {
                    const newPersonnel = [...personnel];
                    newPersonnel[idx] = updated;
                    updateField(['key_personnel'], newPersonnel);
                  }
                }}
                disabled={!editable}
              />
              <Input
                label="Location"
                value={safePerson.location}
                onChange={(e) => {
                  const updated = { ...safePerson, location: e.target.value };
                  if (Array.isArray(formData.key_personnel)) {
                    updateArrayField(['key_personnel'], idx, updated);
                  } else {
                    const newPersonnel = [...personnel];
                    newPersonnel[idx] = updated;
                    updateField(['key_personnel'], newPersonnel);
                  }
                }}
                disabled={!editable}
              />
            </div>
            <ArrayFieldEditor
              label="Designations"
              items={safePerson.designations}
              onAdd={() => {
                const items = [...safePerson.designations, ''];
                const updated = { ...safePerson, designations: items };
                if (Array.isArray(formData.key_personnel)) {
                  updateArrayField(['key_personnel'], idx, updated);
                } else {
                  const newPersonnel = [...personnel];
                  newPersonnel[idx] = updated;
                  updateField(['key_personnel'], newPersonnel);
                }
              }}
              onRemove={(itemIdx) => {
                const items = [...safePerson.designations];
                items.splice(itemIdx, 1);
                const updated = { ...safePerson, designations: items };
                if (Array.isArray(formData.key_personnel)) {
                  updateArrayField(['key_personnel'], idx, updated);
                } else {
                  const newPersonnel = [...personnel];
                  newPersonnel[idx] = updated;
                  updateField(['key_personnel'], newPersonnel);
                }
              }}
              onUpdate={(itemIdx, value) => {
                const items = [...safePerson.designations];
                items[itemIdx] = value;
                const updated = { ...safePerson, designations: items };
                if (Array.isArray(formData.key_personnel)) {
                  updateArrayField(['key_personnel'], idx, updated);
                } else {
                  const newPersonnel = [...personnel];
                  newPersonnel[idx] = updated;
                  updateField(['key_personnel'], newPersonnel);
                }
              }}
              editable={editable}
            />
            <ArrayFieldEditor
              label="Qualifications"
              items={safePerson.qualifications}
              onAdd={() => {
                const items = [...safePerson.qualifications, ''];
                const updated = { ...safePerson, qualifications: items };
                if (Array.isArray(formData.key_personnel)) {
                  updateArrayField(['key_personnel'], idx, updated);
                } else {
                  const newPersonnel = [...personnel];
                  newPersonnel[idx] = updated;
                  updateField(['key_personnel'], newPersonnel);
                }
              }}
              onRemove={(itemIdx) => {
                const items = [...safePerson.qualifications];
                items.splice(itemIdx, 1);
                const updated = { ...safePerson, qualifications: items };
                if (Array.isArray(formData.key_personnel)) {
                  updateArrayField(['key_personnel'], idx, updated);
                } else {
                  const newPersonnel = [...personnel];
                  newPersonnel[idx] = updated;
                  updateField(['key_personnel'], newPersonnel);
                }
              }}
              onUpdate={(itemIdx, value) => {
                const items = [...safePerson.qualifications];
                items[itemIdx] = value;
                const updated = { ...safePerson, qualifications: items };
                if (Array.isArray(formData.key_personnel)) {
                  updateArrayField(['key_personnel'], idx, updated);
                } else {
                  const newPersonnel = [...personnel];
                  newPersonnel[idx] = updated;
                  updateField(['key_personnel'], newPersonnel);
                }
              }}
              editable={editable}
            />
            <ArrayFieldEditor
              label="Expertise Areas"
              items={safePerson.expertise_areas}
              onAdd={() => {
                const items = [...safePerson.expertise_areas, ''];
                const updated = { ...safePerson, expertise_areas: items };
                if (Array.isArray(formData.key_personnel)) {
                  updateArrayField(['key_personnel'], idx, updated);
                } else {
                  const newPersonnel = [...personnel];
                  newPersonnel[idx] = updated;
                  updateField(['key_personnel'], newPersonnel);
                }
              }}
              onRemove={(itemIdx) => {
                const items = [...safePerson.expertise_areas];
                items.splice(itemIdx, 1);
                const updated = { ...safePerson, expertise_areas: items };
                if (Array.isArray(formData.key_personnel)) {
                  updateArrayField(['key_personnel'], idx, updated);
                } else {
                  const newPersonnel = [...personnel];
                  newPersonnel[idx] = updated;
                  updateField(['key_personnel'], newPersonnel);
                }
              }}
              onUpdate={(itemIdx, value) => {
                const items = [...safePerson.expertise_areas];
                items[itemIdx] = value;
                const updated = { ...safePerson, expertise_areas: items };
                if (Array.isArray(formData.key_personnel)) {
                  updateArrayField(['key_personnel'], idx, updated);
                } else {
                  const newPersonnel = [...personnel];
                  newPersonnel[idx] = updated;
                  updateField(['key_personnel'], newPersonnel);
                }
              }}
              editable={editable}
            />
            <div className="grid grid-cols-2 gap-4 mt-4">
              <Input
                label="Email"
                type="email"
                value={safePerson.contact?.email || ''}
                onChange={(e) => {
                  const updated = { ...safePerson, contact: { ...safePerson.contact, email: e.target.value } };
                  if (Array.isArray(formData.key_personnel)) {
                    updateArrayField(['key_personnel'], idx, updated);
                  } else {
                    const newPersonnel = [...personnel];
                    newPersonnel[idx] = updated;
                    updateField(['key_personnel'], newPersonnel);
                  }
                }}
                disabled={!editable}
              />
              <Input
                label="Phone"
                value={safePerson.contact?.phone || ''}
                onChange={(e) => {
                  const updated = { ...safePerson, contact: { ...safePerson.contact, phone: e.target.value } };
                  if (Array.isArray(formData.key_personnel)) {
                    updateArrayField(['key_personnel'], idx, updated);
                  } else {
                    const newPersonnel = [...personnel];
                    newPersonnel[idx] = updated;
                    updateField(['key_personnel'], newPersonnel);
                  }
                }}
                disabled={!editable}
              />
            </div>
          </Card>
        );
      })}
    </div>
  );
}

// Evidence Requirements Section
function EvidenceRequirementsSection({ formData, updateField, updateArrayField, addArrayItem, removeArrayItem, validateField, validationErrors, editable }: any) {
  const er = formData.evidence_requirements || {};
  
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="flex items-center gap-2 mb-2">
            <input
              type="checkbox"
              checked={er.require_citations || false}
              onChange={(e) => updateField(['evidence_requirements', 'require_citations'], e.target.checked)}
              disabled={!editable}
              className="rounded border-theme-border"
            />
            <span className="text-sm font-medium text-theme-text">Require Citations</span>
          </label>
          <p className="text-xs text-theme-text-secondary ml-6">All claims must include citations</p>
        </div>
        <Input
          label="Min Confidence Threshold"
          type="number"
          step="0.1"
          min="0"
          max="1"
          value={er.min_confidence_threshold || 0.7}
          onChange={(e) => updateField(['evidence_requirements', 'min_confidence_threshold'], parseFloat(e.target.value))}
          helperText="Minimum confidence score (0-1) for evidence"
          disabled={!editable}
        />
      </div>
      
      <ArrayFieldEditor
        label="Require Evidence For"
        items={er.require_evidence_for || []}
        onAdd={() => {
          const items = [...(er.require_evidence_for || []), ''];
          updateField(['evidence_requirements', 'require_evidence_for'], items);
        }}
        onRemove={(idx) => {
          const items = [...(er.require_evidence_for || [])];
          items.splice(idx, 1);
          updateField(['evidence_requirements', 'require_evidence_for'], items);
        }}
        onUpdate={(idx, value) => {
          const items = [...(er.require_evidence_for || [])];
          items[idx] = value;
          updateField(['evidence_requirements', 'require_evidence_for'], items);
        }}
        editable={editable}
      />
      
      <ArrayFieldEditor
        label="Allowed Without Evidence"
        items={er.allow_without_evidence || []}
        onAdd={() => {
          const items = [...(er.allow_without_evidence || []), ''];
          updateField(['evidence_requirements', 'allow_without_evidence'], items);
        }}
        onRemove={(idx) => {
          const items = [...(er.allow_without_evidence || [])];
          items.splice(idx, 1);
          updateField(['evidence_requirements', 'allow_without_evidence'], items);
        }}
        onUpdate={(idx, value) => {
          const items = [...(er.allow_without_evidence || [])];
          items[idx] = value;
          updateField(['evidence_requirements', 'allow_without_evidence'], items);
        }}
        editable={editable}
      />
    </div>
  );
}

// Compliance Rules Section
function ComplianceRulesSection({ formData, updateField, updateArrayField, addArrayItem, removeArrayItem, validateField, validationErrors, editable }: any) {
  const rules = formData.compliance_rules || [];
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-theme-text-secondary">Define compliance rules and enforcement levels</p>
        {editable && (
          <Button variant="ghost" size="sm" onClick={() => addArrayItem(['compliance_rules'], {
            rule: '',
            id: '',
            name: '',
            description: '',
            enforcement: 'warning',
            action: '',
            rationale: ''
          })}>
            <Plus className="h-4 w-4 mr-1" />
            Add Rule
          </Button>
        )}
      </div>
      {rules.map((rule: any, idx: number) => (
        <Card key={idx} className="p-4">
          <div className="flex items-start justify-between mb-4">
            <h4 className="font-semibold">Rule {idx + 1}</h4>
            {editable && (
              <Button variant="ghost" size="sm" onClick={() => removeArrayItem(['compliance_rules'], idx)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
          <div className="space-y-3">
            <Input
              label="Rule ID"
              value={rule.id || ''}
              onChange={(e) => updateArrayField(['compliance_rules'], idx, { ...rule, id: e.target.value })}
              disabled={!editable}
            />
            <Input
              label="Name"
              value={rule.name || ''}
              onChange={(e) => updateArrayField(['compliance_rules'], idx, { ...rule, name: e.target.value })}
              disabled={!editable}
            />
            <div>
              <label className="block text-sm font-medium text-theme-text mb-1">Description</label>
              <textarea
                value={rule.description || ''}
                onChange={(e) => updateArrayField(['compliance_rules'], idx, { ...rule, description: e.target.value })}
                className="w-full rounded-lg border border-theme-border px-3 py-2 bg-theme-paper text-theme-text focus:outline-none focus:ring-2 focus:ring-primary-500"
                rows={2}
                disabled={!editable}
              />
            </div>
            <Select
              label="Enforcement"
              value={rule.enforcement || 'warning'}
              onChange={(e) => updateArrayField(['compliance_rules'], idx, { ...rule, enforcement: e.target.value })}
              options={[
                { value: 'block', label: 'Block' },
                { value: 'warning', label: 'Warning' },
                { value: 'enforce', label: 'Enforce' },
                { value: 'validate', label: 'Validate' },
              ]}
              disabled={!editable}
            />
            <div>
              <label className="block text-sm font-medium text-theme-text mb-1">Rationale</label>
              <textarea
                value={rule.rationale || ''}
                onChange={(e) => updateArrayField(['compliance_rules'], idx, { ...rule, rationale: e.target.value })}
                className="w-full rounded-lg border border-theme-border px-3 py-2 bg-theme-paper text-theme-text focus:outline-none focus:ring-2 focus:ring-primary-500"
                rows={2}
                disabled={!editable}
              />
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

// Disclosure Section
function DisclosureSection({ formData, updateField, validateField, validationErrors, editable }: any) {
  const disclosure = formData.disclosure || {};
  
  return (
    <div className="space-y-4">
      <div>
        <label className="flex items-center gap-2 mb-2">
          <input
            type="checkbox"
            checked={disclosure.required || false}
            onChange={(e) => updateField(['disclosure', 'required'], e.target.checked)}
            disabled={!editable}
            className="rounded border-theme-border"
          />
          <span className="text-sm font-medium text-theme-text">Required</span>
        </label>
      </div>
      <Select
        label="Location"
        value={disclosure.location || 'header'}
        onChange={(e) => updateField(['disclosure', 'location'], e.target.value)}
        options={[
          { value: 'header', label: 'Header' },
          { value: 'footer', label: 'Footer' },
          { value: 'separate', label: 'Separate Page' },
        ]}
        disabled={!editable}
      />
      <div>
        <label className="block text-sm font-medium text-theme-text mb-1">Disclosure Text</label>
        <textarea
          value={disclosure.text || ''}
          onChange={(e) => updateField(['disclosure', 'text'], e.target.value)}
          className="w-full rounded-lg border border-theme-border px-3 py-2 bg-theme-paper text-theme-text focus:outline-none focus:ring-2 focus:ring-primary-500 font-mono text-sm"
          rows={15}
          disabled={!editable}
        />
        <p className="mt-1 text-xs text-theme-text-secondary">
          Use variables like {'{timestamp}'}, {'{model_name}'}, {'{reviewer_name}'} for dynamic content
        </p>
      </div>
    </div>
  );
}

// Prohibited Content Section
function ProhibitedContentSection({ formData, updateField, updateArrayField, addArrayItem, removeArrayItem, validateField, validationErrors, editable }: any) {
  // Handle both array format and object format
  const prohibited = formData.prohibited_content;
  const items = Array.isArray(prohibited) ? prohibited : (prohibited ? Object.entries(prohibited).map(([type, value]: [string, any]) => ({
    type,
    ...(typeof value === 'object' ? value : { reason: value })
  })) : []);
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-theme-text-secondary">Define content types that are prohibited in generated documents</p>
        {editable && (
          <Button variant="ghost" size="sm" onClick={() => addArrayItem(['prohibited_content'], {
            type: '',
            reason: '',
            alternative: '',
            enforcement: 'block'
          })}>
            <Plus className="h-4 w-4 mr-1" />
            Add Prohibition
          </Button>
        )}
      </div>
      {items.map((item: any, idx: number) => (
        <Card key={idx} className="p-4">
          <div className="flex items-start justify-between mb-4">
            <h4 className="font-semibold">Prohibition {idx + 1}</h4>
            {editable && (
              <Button variant="ghost" size="sm" onClick={() => {
                if (Array.isArray(prohibited)) {
                  removeArrayItem(['prohibited_content'], idx);
                } else {
                  const newItems = [...items];
                  newItems.splice(idx, 1);
                  const newObj: any = {};
                  newItems.forEach((i: any) => {
                    newObj[i.type] = { reason: i.reason, alternative: i.alternative, enforcement: i.enforcement };
                  });
                  updateField(['prohibited_content'], newObj);
                }
              }}>
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
          <div className="space-y-3">
            <Input
              label="Type"
              value={item.type || ''}
              onChange={(e) => {
                if (Array.isArray(prohibited)) {
                  updateArrayField(['prohibited_content'], idx, { ...item, type: e.target.value });
                } else {
                  const newItems = [...items];
                  newItems[idx] = { ...item, type: e.target.value };
                  const newObj: any = {};
                  newItems.forEach((i: any) => {
                    newObj[i.type] = { reason: i.reason, alternative: i.alternative, enforcement: i.enforcement };
                  });
                  updateField(['prohibited_content'], newObj);
                }
              }}
              disabled={!editable}
            />
            <div>
              <label className="block text-sm font-medium text-theme-text mb-1">Reason</label>
              <textarea
                value={item.reason || ''}
                onChange={(e) => {
                  if (Array.isArray(prohibited)) {
                    updateArrayField(['prohibited_content'], idx, { ...item, reason: e.target.value });
                  } else {
                    const newItems = [...items];
                    newItems[idx] = { ...item, reason: e.target.value };
                    const newObj: any = {};
                    newItems.forEach((i: any) => {
                      newObj[i.type] = { reason: i.reason, alternative: i.alternative, enforcement: i.enforcement };
                    });
                    updateField(['prohibited_content'], newObj);
                  }
                }}
                className="w-full rounded-lg border border-theme-border px-3 py-2 bg-theme-paper text-theme-text focus:outline-none focus:ring-2 focus:ring-primary-500"
                rows={2}
                disabled={!editable}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-theme-text mb-1">Alternative</label>
              <textarea
                value={item.alternative || ''}
                onChange={(e) => {
                  if (Array.isArray(prohibited)) {
                    updateArrayField(['prohibited_content'], idx, { ...item, alternative: e.target.value });
                  } else {
                    const newItems = [...items];
                    newItems[idx] = { ...item, alternative: e.target.value };
                    const newObj: any = {};
                    newItems.forEach((i: any) => {
                      newObj[i.type] = { reason: i.reason, alternative: i.alternative, enforcement: i.enforcement };
                    });
                    updateField(['prohibited_content'], newObj);
                  }
                }}
                className="w-full rounded-lg border border-theme-border px-3 py-2 bg-theme-paper text-theme-text focus:outline-none focus:ring-2 focus:ring-primary-500"
                rows={2}
                disabled={!editable}
              />
            </div>
            <Select
              label="Enforcement"
              value={item.enforcement || 'block'}
              onChange={(e) => {
                if (Array.isArray(prohibited)) {
                  updateArrayField(['prohibited_content'], idx, { ...item, enforcement: e.target.value });
                } else {
                  const newItems = [...items];
                  newItems[idx] = { ...item, enforcement: e.target.value };
                  const newObj: any = {};
                  newItems.forEach((i: any) => {
                    newObj[i.type] = { reason: i.reason, alternative: i.alternative, enforcement: i.enforcement };
                  });
                  updateField(['prohibited_content'], newObj);
                }
              }}
              options={[
                { value: 'block', label: 'Block' },
                { value: 'warning', label: 'Warning' },
                { value: 'transform', label: 'Transform' },
              ]}
              disabled={!editable}
            />
          </div>
        </Card>
      ))}
    </div>
  );
}

// Word Optimization Section
function WordOptimizationSection({ formData, updateField, updateArrayField, addArrayItem, removeArrayItem, validateField, validationErrors, editable }: any) {
  const wo = formData.word_optimization || {};
  
  return (
    <div className="space-y-4">
      <div>
        <label className="flex items-center gap-2 mb-2">
          <input
            type="checkbox"
            checked={wo.enabled || false}
            onChange={(e) => updateField(['word_optimization', 'enabled'], e.target.checked)}
            disabled={!editable}
            className="rounded border-theme-border"
          />
          <span className="text-sm font-medium text-theme-text">Enabled</span>
        </label>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="flex items-center gap-2 mb-2">
            <input
              type="checkbox"
              checked={wo.preserve_citations || false}
              onChange={(e) => updateField(['word_optimization', 'preserve_citations'], e.target.checked)}
              disabled={!editable}
              className="rounded border-theme-border"
            />
            <span className="text-sm font-medium text-theme-text">Preserve Citations</span>
          </label>
        </div>
        <div>
          <label className="flex items-center gap-2 mb-2">
            <input
              type="checkbox"
              checked={wo.preserve_evidence || false}
              onChange={(e) => updateField(['word_optimization', 'preserve_evidence'], e.target.checked)}
              disabled={!editable}
              className="rounded border-theme-border"
            />
            <span className="text-sm font-medium text-theme-text">Preserve Evidence</span>
          </label>
        </div>
      </div>
      <Input
        label="Min Confidence Reduction"
        type="number"
        step="0.1"
        min="0"
        max="1"
        value={wo.min_confidence_reduction || 0.8}
        onChange={(e) => updateField(['word_optimization', 'min_confidence_reduction'], parseFloat(e.target.value))}
        helperText="Minimum confidence threshold after optimization"
        disabled={!editable}
      />
      <Input
        label="Target Reduction Percentage"
        type="number"
        step="0.01"
        min="0"
        max="1"
        value={wo.optimization_rules?.target_reduction_percentage || 0.15}
        onChange={(e) => updateField(['word_optimization', 'optimization_rules', 'target_reduction_percentage'], parseFloat(e.target.value))}
        helperText="Target word count reduction (0-1)"
        disabled={!editable}
      />
    </div>
  );
}

// Required Validations Section
function RequiredValidationsSection({ formData, updateField, updateArrayField, addArrayItem, removeArrayItem, validateField, validationErrors, editable }: any) {
  const validations = formData.required_validations || [];
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-theme-text-secondary">Define validation requirements</p>
        {editable && (
          <Button variant="ghost" size="sm" onClick={() => addArrayItem(['required_validations'], {
            validation: '',
            description: '',
            enforcement: 'blocking',
            min_confidence_threshold: 0.7
          })}>
            <Plus className="h-4 w-4 mr-1" />
            Add Validation
          </Button>
        )}
      </div>
      {validations.map((validation: any, idx: number) => (
        <Card key={idx} className="p-4">
          <div className="flex items-start justify-between mb-4">
            <h4 className="font-semibold">Validation {idx + 1}</h4>
            {editable && (
              <Button variant="ghost" size="sm" onClick={() => removeArrayItem(['required_validations'], idx)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
          <div className="space-y-3">
            <Input
              label="Validation Type"
              value={validation.validation || ''}
              onChange={(e) => updateArrayField(['required_validations'], idx, { ...validation, validation: e.target.value })}
              disabled={!editable}
            />
            <div>
              <label className="block text-sm font-medium text-theme-text mb-1">Description</label>
              <textarea
                value={validation.description || ''}
                onChange={(e) => updateArrayField(['required_validations'], idx, { ...validation, description: e.target.value })}
                className="w-full rounded-lg border border-theme-border px-3 py-2 bg-theme-paper text-theme-text focus:outline-none focus:ring-2 focus:ring-primary-500"
                rows={2}
                disabled={!editable}
              />
            </div>
            <Select
              label="Enforcement"
              value={validation.enforcement || 'blocking'}
              onChange={(e) => updateArrayField(['required_validations'], idx, { ...validation, enforcement: e.target.value })}
              options={[
                { value: 'blocking', label: 'Blocking' },
                { value: 'warning', label: 'Warning' },
                { value: 'enforce', label: 'Enforce' },
              ]}
              disabled={!editable}
            />
            {validation.min_confidence_threshold !== undefined && (
              <Input
                label="Min Confidence Threshold"
                type="number"
                step="0.1"
                min="0"
                max="1"
                value={validation.min_confidence_threshold || 0.7}
                onChange={(e) => updateArrayField(['required_validations'], idx, { ...validation, min_confidence_threshold: parseFloat(e.target.value) })}
                disabled={!editable}
              />
            )}
          </div>
        </Card>
      ))}
    </div>
  );
}

// Data Source Classification Section
function DataSourceClassificationSection({ formData, updateField, validateField, validationErrors, editable }: any) {
  const dsc = formData.data_source_classification || {};
  
  return (
    <div className="space-y-4">
      <div>
        <label className="flex items-center gap-2 mb-2">
          <input
            type="checkbox"
            checked={dsc.enabled || false}
            onChange={(e) => updateField(['data_source_classification', 'enabled'], e.target.checked)}
            disabled={!editable}
            className="rounded border-theme-border"
          />
          <span className="text-sm font-medium text-theme-text">Enabled</span>
        </label>
      </div>
      <p className="text-sm text-theme-text-secondary">
        Data source classification uses filename patterns, folder patterns, and indicators to automatically classify documents.
      </p>
    </div>
  );
}

// Compliance Section
function ComplianceSection({ formData, updateField, updateArrayField, addArrayItem, removeArrayItem, validateField, validationErrors, editable }: any) {
  const compliance = formData.compliance || {};
  
  return (
    <div className="space-y-4">
      <Input
        label="Reporting Frequency"
        value={compliance.reporting_frequency || ''}
        onChange={(e) => updateField(['compliance', 'reporting_frequency'], e.target.value)}
        helperText="How often compliance reports are generated"
        disabled={!editable}
      />
      <Input
        label="Responsible Authority"
        value={compliance.responsible_authority || ''}
        onChange={(e) => updateField(['compliance', 'responsible_authority'], e.target.value)}
        disabled={!editable}
      />
      <div>
        <label className="block text-sm font-medium text-theme-text mb-2">Required Reports</label>
        <ArrayFieldEditor
          label=""
          items={compliance.required_reports || []}
          onAdd={() => {
            const items = [...(compliance.required_reports || []), ''];
            updateField(['compliance', 'required_reports'], items);
          }}
          onRemove={(idx: number) => {
            const items = [...(compliance.required_reports || [])];
            items.splice(idx, 1);
            updateField(['compliance', 'required_reports'], items);
          }}
          onUpdate={(idx: number, value: string) => {
            const items = [...(compliance.required_reports || [])];
            items[idx] = value;
            updateField(['compliance', 'required_reports'], items);
          }}
          editable={editable}
        />
      </div>
      {compliance.citation_requirements && (
        <Card className="p-4">
          <h4 className="font-semibold mb-4">Citation Requirements</h4>
          <div className="space-y-3">
            <Input
              label="Evidence Confidence Threshold"
              type="number"
              step="0.1"
              min="0"
              max="1"
              value={compliance.citation_requirements.evidence_confidence_threshold || 0.7}
              onChange={(e) => updateField(['compliance', 'citation_requirements', 'evidence_confidence_threshold'], parseFloat(e.target.value))}
              disabled={!editable}
            />
            <Input
              label="Minimum Sources Per Claim"
              type="number"
              value={compliance.citation_requirements.minimum_sources_per_claim || 2}
              onChange={(e) => updateField(['compliance', 'citation_requirements', 'minimum_sources_per_claim'], parseInt(e.target.value))}
              disabled={!editable}
            />
            <ArrayFieldEditor
              label="Preferred Sources"
              items={compliance.citation_requirements.preferred_sources || []}
              onAdd={() => {
                const items = [...(compliance.citation_requirements.preferred_sources || []), ''];
                updateField(['compliance', 'citation_requirements', 'preferred_sources'], items);
              }}
              onRemove={(idx: number) => {
                const items = [...(compliance.citation_requirements.preferred_sources || [])];
                items.splice(idx, 1);
                updateField(['compliance', 'citation_requirements', 'preferred_sources'], items);
              }}
              onUpdate={(idx: number, value: string) => {
                const items = [...(compliance.citation_requirements.preferred_sources || [])];
                items[idx] = value;
                updateField(['compliance', 'citation_requirements', 'preferred_sources'], items);
              }}
              editable={editable}
            />
          </div>
        </Card>
      )}
      {compliance.audit_requirements && (
        <Card className="p-4">
          <h4 className="font-semibold mb-4">Audit Requirements</h4>
          <div className="space-y-3">
            <div>
              <label className="flex items-center gap-2 mb-2">
                <input
                  type="checkbox"
                  checked={compliance.audit_requirements.log_all_ai_operations || false}
                  onChange={(e) => updateField(['compliance', 'audit_requirements', 'log_all_ai_operations'], e.target.checked)}
                  disabled={!editable}
                  className="rounded border-theme-border"
                />
                <span className="text-sm font-medium text-theme-text">Log All AI Operations</span>
              </label>
            </div>
            <div>
              <label className="flex items-center gap-2 mb-2">
                <input
                  type="checkbox"
                  checked={compliance.audit_requirements.track_evidence_usage || false}
                  onChange={(e) => updateField(['compliance', 'audit_requirements', 'track_evidence_usage'], e.target.checked)}
                  disabled={!editable}
                  className="rounded border-theme-border"
                />
                <span className="text-sm font-medium text-theme-text">Track Evidence Usage</span>
              </label>
            </div>
            <div>
              <label className="flex items-center gap-2 mb-2">
                <input
                  type="checkbox"
                  checked={compliance.audit_requirements.record_review_decisions || false}
                  onChange={(e) => updateField(['compliance', 'audit_requirements', 'record_review_decisions'], e.target.checked)}
                  disabled={!editable}
                  className="rounded border-theme-border"
                />
                <span className="text-sm font-medium text-theme-text">Record Review Decisions</span>
              </label>
            </div>
            <Input
              label="Retention Period (Months)"
              type="number"
              value={compliance.audit_requirements.retention_period_months || 12}
              onChange={(e) => updateField(['compliance', 'audit_requirements', 'retention_period_months'], parseInt(e.target.value))}
              disabled={!editable}
            />
          </div>
        </Card>
      )}
    </div>
  );
}

// Entity Governance Section
function EntityGovernanceSection({ formData, updateField, updateArrayField, addArrayItem, removeArrayItem, validateField, validationErrors, editable }: any) {
  const eg = formData.entity_governance || {};
  
  // Graceful degradation: If section doesn't exist, show initialization message
  if (!formData.entity_governance) {
    return (
      <div className="p-4 text-center text-theme-text-secondary">
        <Shield className="h-12 w-12 mx-auto mb-3 opacity-50" />
        <p className="text-sm mb-2">Entity governance is not configured for this policy pack.</p>
        <p className="text-xs mb-4">Entity governance provides automated data quality and classification rules.</p>
        {editable && (
          <Button variant="ghost" size="sm" onClick={() => updateField(['entity_governance'], {
            quarantine_rules: [],
            source_type_mapping: {
              internal_document: [],
              external_government_opportunity: [],
              external_commercial_opportunity: [],
              unclassified: []
            },
            enrichment: {
              basic: { enabled: true, functions: [] },
              deep: { enabled: false, schedule: 'daily' },
              on_demand: { enabled: true }
            },
            quality_thresholds: {
              min_confidence: 0.7,
              min_completeness: 0.6
            }
          })}>
            Initialize Entity Governance
          </Button>
        )}
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-blue-900 dark:text-blue-100 text-sm mb-1">Entity Governance System</div>
            <p className="text-xs text-blue-700 dark:text-blue-300">
              Automated rules to classify, quarantine, and enrich entities based on source type and data quality.
            </p>
          </div>
        </div>
      </div>

      {/* Quarantine Rules */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            Quarantine Rules
          </h4>
          {editable && (
            <Button variant="ghost" size="sm" onClick={() => {
              const rules = [...(eg.quarantine_rules || []), {
                id: `rule_${Date.now()}`,
                name: 'New Rule',
                description: '',
                entity_types: [],
                source_types: [],
                action: 'quarantine',
                threshold: 0.7
              }];
              updateField(['entity_governance', 'quarantine_rules'], rules);
            }}>
              <Plus className="h-4 w-4 mr-1" />
              Add Rule
            </Button>
          )}
        </div>
        <div className="space-y-3">
          {(eg.quarantine_rules || []).map((rule: any, idx: number) => (
            <Card key={idx} className="p-3">
              <div className="flex items-start justify-between mb-3">
                <h5 className="font-medium">{rule.name || `Rule ${idx + 1}`}</h5>
                {editable && (
                  <Button variant="ghost" size="sm" onClick={() => {
                    const rules = [...(eg.quarantine_rules || [])];
                    rules.splice(idx, 1);
                    updateField(['entity_governance', 'quarantine_rules'], rules);
                  }}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Rule ID"
                  value={rule.id || ''}
                  onChange={(e) => {
                    const rules = [...(eg.quarantine_rules || [])];
                    rules[idx] = { ...rule, id: e.target.value };
                    updateField(['entity_governance', 'quarantine_rules'], rules);
                  }}
                  disabled={!editable}
                />
                <Input
                  label="Name"
                  value={rule.name || ''}
                  onChange={(e) => {
                    const rules = [...(eg.quarantine_rules || [])];
                    rules[idx] = { ...rule, name: e.target.value };
                    updateField(['entity_governance', 'quarantine_rules'], rules);
                  }}
                  disabled={!editable}
                />
              </div>
              <div className="mt-3">
                <label className="block text-sm font-medium text-theme-text mb-1">Description</label>
                <textarea
                  value={rule.description || ''}
                  onChange={(e) => {
                    const rules = [...(eg.quarantine_rules || [])];
                    rules[idx] = { ...rule, description: e.target.value };
                    updateField(['entity_governance', 'quarantine_rules'], rules);
                  }}
                  className="w-full rounded-lg border border-theme-border px-3 py-2 bg-theme-paper text-theme-text focus:outline-none focus:ring-2 focus:ring-primary-500"
                  rows={2}
                  disabled={!editable}
                />
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <label className="block text-sm font-medium text-theme-text mb-1">Action</label>
                  <Select
                    value={rule.action || 'quarantine'}
                    onChange={(e) => {
                      const rules = [...(eg.quarantine_rules || [])];
                      rules[idx] = { ...rule, action: e.target.value };
                      updateField(['entity_governance', 'quarantine_rules'], rules);
                    }}
                    options={[
                      { value: 'quarantine', label: 'Quarantine' },
                      { value: 'reject', label: 'Reject' },
                    ]}
                    disabled={!editable}
                  />
                </div>
                <Input
                  label="Threshold"
                  type="number"
                  step="0.1"
                  min="0"
                  max="1"
                  value={rule.threshold || 0.7}
                  onChange={(e) => {
                    const rules = [...(eg.quarantine_rules || [])];
                    rules[idx] = { ...rule, threshold: parseFloat(e.target.value) };
                    updateField(['entity_governance', 'quarantine_rules'], rules);
                  }}
                  disabled={!editable}
                />
              </div>
              <ArrayFieldEditor
                label="Entity Types"
                items={rule.entity_types || []}
                onAdd={() => {
                  const rules = [...(eg.quarantine_rules || [])];
                  rules[idx] = { ...rule, entity_types: [...(rule.entity_types || []), ''] };
                  updateField(['entity_governance', 'quarantine_rules'], rules);
                }}
                onRemove={(itemIdx) => {
                  const rules = [...(eg.quarantine_rules || [])];
                  const entityTypes = [...(rule.entity_types || [])];
                  entityTypes.splice(itemIdx, 1);
                  rules[idx] = { ...rule, entity_types: entityTypes };
                  updateField(['entity_governance', 'quarantine_rules'], rules);
                }}
                onUpdate={(itemIdx, value) => {
                  const rules = [...(eg.quarantine_rules || [])];
                  const entityTypes = [...(rule.entity_types || [])];
                  entityTypes[itemIdx] = value;
                  rules[idx] = { ...rule, entity_types: entityTypes };
                  updateField(['entity_governance', 'quarantine_rules'], rules);
                }}
                editable={editable}
              />
              <ArrayFieldEditor
                label="Source Types"
                items={rule.source_types || []}
                onAdd={() => {
                  const rules = [...(eg.quarantine_rules || [])];
                  rules[idx] = { ...rule, source_types: [...(rule.source_types || []), ''] };
                  updateField(['entity_governance', 'quarantine_rules'], rules);
                }}
                onRemove={(itemIdx) => {
                  const rules = [...(eg.quarantine_rules || [])];
                  const sourceTypes = [...(rule.source_types || [])];
                  sourceTypes.splice(itemIdx, 1);
                  rules[idx] = { ...rule, source_types: sourceTypes };
                  updateField(['entity_governance', 'quarantine_rules'], rules);
                }}
                onUpdate={(itemIdx, value) => {
                  const rules = [...(eg.quarantine_rules || [])];
                  const sourceTypes = [...(rule.source_types || [])];
                  sourceTypes[itemIdx] = value;
                  rules[idx] = { ...rule, source_types: sourceTypes };
                  updateField(['entity_governance', 'quarantine_rules'], rules);
                }}
                editable={editable}
              />
            </Card>
          ))}
          {(eg.quarantine_rules || []).length === 0 && (
            <p className="text-sm text-theme-text-secondary italic text-center py-4">
              No quarantine rules defined. Click "Add Rule" to create one.
            </p>
          )}
        </div>
      </Card>

      {/* Source Type Mapping */}
      <Card className="p-4">
        <h4 className="font-semibold mb-4 flex items-center gap-2">
          <Filter className="h-4 w-4" />
          Source Type Mapping
        </h4>
        <p className="text-sm text-theme-text-secondary mb-4">
          Define which entity types belong to each source classification.
        </p>
        <div className="space-y-4">
          {['internal_document', 'external_government_opportunity', 'external_commercial_opportunity', 'unclassified'].map((sourceType) => (
            <div key={sourceType}>
              <label className="block text-sm font-medium text-theme-text mb-2 capitalize">
                {sourceType.replace(/_/g, ' ')}
              </label>
              <ArrayFieldEditor
                label=""
                items={eg.source_type_mapping?.[sourceType] || []}
                onAdd={() => {
                  const mapping = { ...(eg.source_type_mapping || {}) };
                  mapping[sourceType] = [...(mapping[sourceType] || []), ''];
                  updateField(['entity_governance', 'source_type_mapping'], mapping);
                }}
                onRemove={(idx) => {
                  const mapping = { ...(eg.source_type_mapping || {}) };
                  const items = [...(mapping[sourceType] || [])];
                  items.splice(idx, 1);
                  mapping[sourceType] = items;
                  updateField(['entity_governance', 'source_type_mapping'], mapping);
                }}
                onUpdate={(idx, value) => {
                  const mapping = { ...(eg.source_type_mapping || {}) };
                  const items = [...(mapping[sourceType] || [])];
                  items[idx] = value;
                  mapping[sourceType] = items;
                  updateField(['entity_governance', 'source_type_mapping'], mapping);
                }}
                editable={editable}
              />
            </div>
          ))}
        </div>
      </Card>

      {/* Enrichment Configuration */}
      <Card className="p-4">
        <h4 className="font-semibold mb-4 flex items-center gap-2">
          <Zap className="h-4 w-4" />
          Enrichment Configuration
        </h4>
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-theme-text">Basic Enrichment</label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={eg.enrichment?.basic?.enabled || false}
                  onChange={(e) => updateField(['entity_governance', 'enrichment', 'basic', 'enabled'], e.target.checked)}
                  disabled={!editable}
                  className="rounded border-theme-border"
                />
                <span className="text-sm text-theme-text">Enabled</span>
              </label>
            </div>
            <p className="text-xs text-theme-text-secondary mb-2">
              Automatic field normalization (dates, currency, locations, names)
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-theme-text">Deep Enrichment</label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={eg.enrichment?.deep?.enabled || false}
                  onChange={(e) => updateField(['entity_governance', 'enrichment', 'deep', 'enabled'], e.target.checked)}
                  disabled={!editable}
                  className="rounded border-theme-border"
                />
                <span className="text-sm text-theme-text">Enabled</span>
              </label>
            </div>
            <p className="text-xs text-theme-text-secondary mb-2">
              Advanced enrichment: deduplication, entity linking, semantic tagging
            </p>
            {eg.enrichment?.deep?.enabled && (
              <Input
                label="Schedule"
                value={eg.enrichment.deep.schedule || 'daily'}
                onChange={(e) => updateField(['entity_governance', 'enrichment', 'deep', 'schedule'], e.target.value)}
                helperText="Cron expression or 'daily', 'weekly', 'monthly'"
                disabled={!editable}
              />
            )}
          </div>
        </div>
      </Card>

      {/* Quality Thresholds */}
      <Card className="p-4">
        <h4 className="font-semibold mb-4 flex items-center gap-2">
          <CheckCircle className="h-4 w-4" />
          Quality Thresholds
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Minimum Confidence"
            type="number"
            step="0.1"
            min="0"
            max="1"
            value={eg.quality_thresholds?.min_confidence || 0.7}
            onChange={(e) => updateField(['entity_governance', 'quality_thresholds', 'min_confidence'], parseFloat(e.target.value))}
            helperText="Minimum confidence score (0-1)"
            disabled={!editable}
          />
          <Input
            label="Minimum Completeness"
            type="number"
            step="0.1"
            min="0"
            max="1"
            value={eg.quality_thresholds?.min_completeness || 0.6}
            onChange={(e) => updateField(['entity_governance', 'quality_thresholds', 'min_completeness'], parseFloat(e.target.value))}
            helperText="Minimum field completeness (0-1)"
            disabled={!editable}
          />
        </div>
      </Card>
    </div>
  );
}

// Data Source Configurations Section
function DataSourceConfigurationsSection({ formData, updateField, updateArrayField, addArrayItem, removeArrayItem, validateField, validationErrors, editable }: any) {
  const dsc = formData.data_source_configurations || {};
  
  return (
    <div className="space-y-6">
      <Card className="p-4">
        <h4 className="font-semibold mb-4">Keywords</h4>
        <ArrayFieldEditor
          label="Keywords"
          items={dsc.keywords || []}
          onAdd={() => {
            const items = [...(dsc.keywords || []), ''];
            updateField(['data_source_configurations', 'keywords'], items);
          }}
          onRemove={(idx) => {
            const items = [...(dsc.keywords || [])];
            items.splice(idx, 1);
            updateField(['data_source_configurations', 'keywords'], items);
          }}
          onUpdate={(idx, value) => {
            const items = [...(dsc.keywords || [])];
            items[idx] = value;
            updateField(['data_source_configurations', 'keywords'], items);
          }}
          editable={editable}
        />
      </Card>

      <Card className="p-4">
        <h4 className="font-semibold mb-4 flex items-center gap-2">
          <MapPin className="h-4 w-4" />
          Geographic Preferences
        </h4>
        <div className="space-y-4">
          <ArrayFieldEditor
            label="Provinces"
            items={dsc.geographic_preferences?.provinces || []}
            onAdd={() => {
              const items = [...(dsc.geographic_preferences?.provinces || []), ''];
              updateField(['data_source_configurations', 'geographic_preferences', 'provinces'], items);
            }}
            onRemove={(idx) => {
              const items = [...(dsc.geographic_preferences?.provinces || [])];
              items.splice(idx, 1);
              updateField(['data_source_configurations', 'geographic_preferences', 'provinces'], items);
            }}
            onUpdate={(idx, value) => {
              const items = [...(dsc.geographic_preferences?.provinces || [])];
              items[idx] = value;
              updateField(['data_source_configurations', 'geographic_preferences', 'provinces'], items);
            }}
            editable={editable}
          />
          <div>
            <label className="block text-sm font-medium text-theme-text mb-2">Priority Cities - BC</label>
            <ArrayFieldEditor
              label=""
              items={dsc.geographic_preferences?.priority_cities?.bc || []}
              onAdd={() => {
                const cities = { ...(dsc.geographic_preferences?.priority_cities || {}), bc: [...(dsc.geographic_preferences?.priority_cities?.bc || []), ''] };
                updateField(['data_source_configurations', 'geographic_preferences', 'priority_cities'], cities);
              }}
              onRemove={(idx) => {
                const items = [...(dsc.geographic_preferences?.priority_cities?.bc || [])];
                items.splice(idx, 1);
                const cities = { ...(dsc.geographic_preferences?.priority_cities || {}), bc: items };
                updateField(['data_source_configurations', 'geographic_preferences', 'priority_cities'], cities);
              }}
              onUpdate={(idx, value) => {
                const items = [...(dsc.geographic_preferences?.priority_cities?.bc || [])];
                items[idx] = value;
                const cities = { ...(dsc.geographic_preferences?.priority_cities || {}), bc: items };
                updateField(['data_source_configurations', 'geographic_preferences', 'priority_cities'], cities);
              }}
              editable={editable}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-theme-text mb-2">Priority Cities - Ontario</label>
            <ArrayFieldEditor
              label=""
              items={dsc.geographic_preferences?.priority_cities?.ontario || []}
              onAdd={() => {
                const cities = { ...(dsc.geographic_preferences?.priority_cities || {}), ontario: [...(dsc.geographic_preferences?.priority_cities?.ontario || []), ''] };
                updateField(['data_source_configurations', 'geographic_preferences', 'priority_cities'], cities);
              }}
              onRemove={(idx) => {
                const items = [...(dsc.geographic_preferences?.priority_cities?.ontario || [])];
                items.splice(idx, 1);
                const cities = { ...(dsc.geographic_preferences?.priority_cities || {}), ontario: items };
                updateField(['data_source_configurations', 'geographic_preferences', 'priority_cities'], cities);
              }}
              onUpdate={(idx, value) => {
                const items = [...(dsc.geographic_preferences?.priority_cities?.ontario || [])];
                items[idx] = value;
                const cities = { ...(dsc.geographic_preferences?.priority_cities || {}), ontario: items };
                updateField(['data_source_configurations', 'geographic_preferences', 'priority_cities'], cities);
              }}
              editable={editable}
            />
          </div>
        </div>
      </Card>
    </div>
  );
}

// Report Types Section
function ReportTypesSection({ formData, updateField, updateArrayField, addArrayItem, removeArrayItem, validateField, validationErrors, editable }: any) {
  const reportTypes = formData.report_types || [];
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-theme-text-secondary">Configure report types and their sections</p>
        {editable && (
          <Button variant="ghost" size="sm" onClick={() => addArrayItem(['report_types'], {
            id: '',
            name: '',
            description: '',
            category: '',
            default_status: 'draft',
            workflow_stages: [],
            sections: [],
            default_questions: []
          })}>
            <Plus className="h-4 w-4 mr-1" />
            Add Report Type
          </Button>
        )}
      </div>
      {reportTypes.map((rt: any, idx: number) => (
        <Card key={idx} className="p-4">
          <div className="flex items-start justify-between mb-4">
            <h4 className="font-semibold">{rt.name || `Report Type ${idx + 1}`}</h4>
            {editable && (
              <Button variant="ghost" size="sm" onClick={() => removeArrayItem(['report_types'], idx)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
          <div className="space-y-3">
            <Input
              label="ID"
              value={rt.id || ''}
              onChange={(e) => updateArrayField(['report_types'], idx, { ...rt, id: e.target.value })}
              disabled={!editable}
            />
            <Input
              label="Name"
              value={rt.name || ''}
              onChange={(e) => updateArrayField(['report_types'], idx, { ...rt, name: e.target.value })}
              disabled={!editable}
            />
            <div>
              <label className="block text-sm font-medium text-theme-text mb-1">Description</label>
              <textarea
                value={rt.description || ''}
                onChange={(e) => updateArrayField(['report_types'], idx, { ...rt, description: e.target.value })}
                className="w-full rounded-lg border border-theme-border px-3 py-2 bg-theme-paper text-theme-text focus:outline-none focus:ring-2 focus:ring-primary-500"
                rows={2}
                disabled={!editable}
              />
            </div>
            <Input
              label="Category"
              value={rt.category || ''}
              onChange={(e) => updateArrayField(['report_types'], idx, { ...rt, category: e.target.value })}
              disabled={!editable}
            />
            <Select
              label="Default Status"
              value={rt.default_status || 'draft'}
              onChange={(e) => updateArrayField(['report_types'], idx, { ...rt, default_status: e.target.value })}
              options={[
                { value: 'draft', label: 'Draft' },
                { value: 'in_review', label: 'In Review' },
                { value: 'approved', label: 'Approved' },
                { value: 'submitted', label: 'Submitted' },
                { value: 'archived', label: 'Archived' },
              ]}
              disabled={!editable}
            />
            <ArrayFieldEditor
              label="Workflow Stages"
              items={rt.workflow_stages || []}
              onAdd={() => {
                const items = [...(rt.workflow_stages || []), ''];
                updateArrayField(['report_types'], idx, { ...rt, workflow_stages: items });
              }}
              onRemove={(itemIdx) => {
                const items = [...(rt.workflow_stages || [])];
                items.splice(itemIdx, 1);
                updateArrayField(['report_types'], idx, { ...rt, workflow_stages: items });
              }}
              onUpdate={(itemIdx, value) => {
                const items = [...(rt.workflow_stages || [])];
                items[itemIdx] = value;
                updateArrayField(['report_types'], idx, { ...rt, workflow_stages: items });
              }}
              editable={editable}
            />
            
            {/* Template Generation Prompts */}
            {rt.template_generation_prompts && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <h5 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <Code className="h-4 w-4" />
                  Template Generation Prompts
                </h5>
                <div className="space-y-3">
                  {Object.keys(rt.template_generation_prompts).map((promptKey: string) => {
                    const prompt = rt.template_generation_prompts[promptKey];
                    return (
                      <Card key={promptKey} className="p-3 bg-gray-50 dark:bg-gray-800">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium capitalize">{promptKey}</span>
                            {prompt.enabled !== undefined && (
                              <Badge variant={prompt.enabled ? "success" : "secondary"}>
                                {prompt.enabled ? "Enabled" : "Disabled"}
                              </Badge>
                            )}
                          </div>
                        </div>
                        {prompt.name && (
                          <div className="text-sm font-medium text-theme-text mb-1">{prompt.name}</div>
                        )}
                        {prompt.description && (
                          <div className="text-xs text-theme-text-secondary mb-2">{prompt.description}</div>
                        )}
                        {prompt.prompt && (
                          <div className="mt-2">
                            <div className="text-xs text-theme-text-secondary mb-1">Prompt Preview:</div>
                            <div className="text-xs font-mono bg-gray-100 dark:bg-gray-900 p-2 rounded max-h-32 overflow-y-auto">
                              {prompt.prompt.substring(0, 200)}...
                            </div>
                          </div>
                        )}
                        {prompt.variables && Object.keys(prompt.variables).length > 0 && (
                          <div className="mt-2">
                            <div className="text-xs text-theme-text-secondary mb-1">Variables ({Object.keys(prompt.variables).length}):</div>
                            <div className="flex flex-wrap gap-1">
                              {Object.keys(prompt.variables).slice(0, 5).map((varKey: string) => (
                                <Badge key={varKey} variant="outline" className="text-xs">
                                  {varKey}
                                </Badge>
                              ))}
                              {Object.keys(prompt.variables).length > 5 && (
                                <Badge variant="outline" className="text-xs">
                                  +{Object.keys(prompt.variables).length - 5} more
                                </Badge>
                              )}
                            </div>
                          </div>
                        )}
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Workplan Generation: Service Line Templates - Enhanced Display */}
            {rt.id === 'workplan_generation' && rt.service_line_templates && (
              <WorkplanTemplatesDisplay 
                serviceLineTemplates={rt.service_line_templates}
                editable={editable}
              />
            )}
          </div>
        </Card>
      ))}
    </div>
  );
}

// Entity Extraction Section
function EntityExtractionSection({ formData, updateField, validateField, validationErrors, editable }: any) {
  const ee = formData.entity_extraction || {};
  const entityTypes = Object.keys(ee.entity_types || {});
  
  return (
    <div className="space-y-6">
      <div>
        <label className="flex items-center gap-2 mb-2">
          <input
            type="checkbox"
            checked={ee.enabled || false}
            onChange={(e) => updateField(['entity_extraction', 'enabled'], e.target.checked)}
            disabled={!editable}
            className="rounded border-theme-border"
          />
          <span className="text-sm font-medium text-theme-text">Enabled</span>
        </label>
      </div>
      <Input
        label="Model"
        value={ee.model || 'gpt-4'}
        onChange={(e) => updateField(['entity_extraction', 'model'], e.target.value)}
        helperText="AI model to use for entity extraction"
        disabled={!editable}
      />
      <Card className="p-4">
        <h4 className="font-semibold mb-4">Entity Types</h4>
        <div className="space-y-2">
          {entityTypes.map((entityType: string) => (
            <div key={entityType} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded">
              <span className="text-sm font-medium">{entityType}</span>
              <Badge variant="default">{ee.entity_types[entityType]?.fields?.length || 0} fields</Badge>
            </div>
          ))}
        </div>
        <p className="text-xs text-theme-text-secondary mt-4">
          Entity type configuration is complex. Use the YAML editor for detailed configuration.
        </p>
      </Card>
      <Card className="p-4">
        <h4 className="font-semibold mb-4">Prompts</h4>
        <p className="text-sm text-theme-text-secondary mb-4">
          Entity extraction prompts are configured per entity type. Use the YAML editor for detailed prompt editing.
        </p>
        <div className="space-y-2">
          {Object.keys(ee.prompts || {}).slice(0, 5).map((promptKey: string) => (
            <div key={promptKey} className="text-sm">
              <span className="font-medium">{promptKey}:</span>
              <span className="text-theme-text-secondary ml-2">
                {(ee.prompts[promptKey] || '').substring(0, 100)}...
              </span>
            </div>
          ))}
          {Object.keys(ee.prompts || {}).length > 5 && (
            <p className="text-xs text-theme-text-secondary">
              +{Object.keys(ee.prompts || {}).length - 5} more prompts
            </p>
          )}
        </div>
      </Card>
    </div>
  );
}

// Query Samples Section
function QuerySamplesSection({ formData, updateField, updateArrayField, addArrayItem, removeArrayItem, validateField, validationErrors, editable }: any) {
  const qs = formData.query_samples || {};
  
  return (
    <div className="space-y-6">
      {Object.keys(qs).map((category: string) => (
        <Card key={category} className="p-4">
          <h4 className="font-semibold mb-4 capitalize">{category.replace(/_/g, ' ')}</h4>
          {Array.isArray(qs[category]) ? (
            <ArrayFieldEditor
              label=""
              items={qs[category]}
              onAdd={() => {
                const items = [...(qs[category] || []), ''];
                updateField(['query_samples', category], items);
              }}
              onRemove={(idx) => {
                const items = [...(qs[category] || [])];
                items.splice(idx, 1);
                updateField(['query_samples', category], items);
              }}
              onUpdate={(idx, value) => {
                const items = [...(qs[category] || [])];
                items[idx] = value;
                updateField(['query_samples', category], items);
              }}
              editable={editable}
            />
          ) : (
            <div className="space-y-4">
              {Object.keys(qs[category] || {}).map((subCategory: string) => (
                <div key={subCategory}>
                  <h5 className="text-sm font-medium mb-2 capitalize">{subCategory.replace(/_/g, ' ')}</h5>
                  <ArrayFieldEditor
                    label=""
                    items={qs[category][subCategory] || []}
                    onAdd={() => {
                      const items = [...(qs[category][subCategory] || []), ''];
                      updateField(['query_samples', category, subCategory], items);
                    }}
                    onRemove={(idx) => {
                      const items = [...(qs[category][subCategory] || [])];
                      items.splice(idx, 1);
                      updateField(['query_samples', category, subCategory], items);
                    }}
                    onUpdate={(idx, value) => {
                      const items = [...(qs[category][subCategory] || [])];
                      items[idx] = value;
                      updateField(['query_samples', category, subCategory], items);
                    }}
                    editable={editable}
                  />
                </div>
              ))}
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}

// Report Templates Section
function ReportTemplatesSection({ formData, updateField, validateField, validationErrors, editable }: any) {
  const templates = formData.report_templates || {};
  
  return (
    <div className="space-y-4">
      {Object.keys(templates).map((templateKey: string) => {
        const template = templates[templateKey];
        return (
          <Card key={templateKey} className="p-4">
            <h4 className="font-semibold mb-4">{template.name || templateKey}</h4>
            <div className="space-y-3">
              <Input
                label="Name"
                value={template.name || ''}
                onChange={(e) => updateField(['report_templates', templateKey, 'name'], e.target.value)}
                disabled={!editable}
              />
              <div>
                <label className="block text-sm font-medium text-theme-text mb-1">Description</label>
                <textarea
                  value={template.description || ''}
                  onChange={(e) => updateField(['report_templates', templateKey, 'description'], e.target.value)}
                  className="w-full rounded-lg border border-theme-border px-3 py-2 bg-theme-paper text-theme-text focus:outline-none focus:ring-2 focus:ring-primary-500"
                  rows={2}
                  disabled={!editable}
                />
              </div>
              <Input
                label="Category"
                value={template.category || ''}
                onChange={(e) => updateField(['report_templates', templateKey, 'category'], e.target.value)}
                disabled={!editable}
              />
              <Input
                label="Frequency"
                value={template.frequency || ''}
                onChange={(e) => updateField(['report_templates', templateKey, 'frequency'], e.target.value)}
                disabled={!editable}
              />
              {template.questions && Array.isArray(template.questions) && (
                <div>
                  <label className="block text-sm font-medium text-theme-text mb-2">Questions ({template.questions.length})</label>
                  <div className="space-y-2">
                    {template.questions.map((q: any, qIdx: number) => (
                      <div key={qIdx} className="p-2 bg-gray-50 dark:bg-gray-800 rounded text-sm">
                        <div className="font-medium">{q.section}</div>
                        <div className="text-theme-text-secondary mt-1">{q.question}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Card>
        );
      })}
    </div>
  );
}

// Conversational Context Section
function ConversationalContextSection({ formData, updateField, validateField, validationErrors, editable }: any) {
  const cc = formData.conversational_context || {};
  
  return (
    <div className="space-y-4">
      <div>
        <label className="flex items-center gap-2 mb-2">
          <input
            type="checkbox"
            checked={cc.enabled || false}
            onChange={(e) => updateField(['conversational_context', 'enabled'], e.target.checked)}
            disabled={!editable}
            className="rounded border-theme-border"
          />
          <span className="text-sm font-medium text-theme-text">Enabled</span>
        </label>
      </div>
      <Input
        label="Max History"
        type="number"
        value={cc.max_history || 10}
        onChange={(e) => updateField(['conversational_context', 'max_history'], parseInt(e.target.value))}
        helperText="Maximum number of conversation turns to remember"
        disabled={!editable}
      />
      <Input
        label="Confidence Trigger"
        type="number"
        step="0.01"
        min="0"
        max="1"
        value={cc.confidence_trigger || 0.65}
        onChange={(e) => updateField(['conversational_context', 'confidence_trigger'], parseFloat(e.target.value))}
        helperText="Confidence threshold for using context (0-1)"
        disabled={!editable}
      />
      <div>
        <label className="flex items-center gap-2 mb-2">
          <input
            type="checkbox"
            checked={cc.deep_history_enabled || false}
            onChange={(e) => updateField(['conversational_context', 'deep_history_enabled'], e.target.checked)}
            disabled={!editable}
            className="rounded border-theme-border"
          />
          <span className="text-sm font-medium text-theme-text">Deep History Enabled</span>
        </label>
      </div>
      <Input
        label="Recency Decay Hours"
        type="number"
        value={cc.recency_decay_hours || 720}
        onChange={(e) => updateField(['conversational_context', 'recency_decay_hours'], parseInt(e.target.value))}
        helperText="Hours after which context relevance decays"
        disabled={!editable}
      />
      <div>
        <label className="block text-sm font-medium text-theme-text mb-1">Explanation</label>
        <textarea
          value={cc.explanation || ''}
          onChange={(e) => updateField(['conversational_context', 'explanation'], e.target.value)}
          className="w-full rounded-lg border border-theme-border px-3 py-2 bg-theme-paper text-theme-text focus:outline-none focus:ring-2 focus:ring-primary-500"
          rows={3}
          disabled={!editable}
        />
      </div>
    </div>
  );
}

// Workplan Templates Display Component - Enhanced, Scalable, Production-Ready
interface WorkplanTemplatesDisplayProps {
  serviceLineTemplates: Record<string, any>;
  editable?: boolean;
}

function WorkplanTemplatesDisplay({ serviceLineTemplates, editable = false }: WorkplanTemplatesDisplayProps) {
  const [expandedTemplates, setExpandedTemplates] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedServiceLine, setSelectedServiceLine] = useState<string | null>(null);
  
  const templateEntries = Object.entries(serviceLineTemplates);
  const totalTemplates = templateEntries.length;
  const totalSections = templateEntries.reduce((sum, [, t]) => sum + (t.additional_sections?.length || 0), 0);
  const totalQuestions = templateEntries.reduce((sum, [, t]) => sum + (t.additional_questions?.length || 0), 0);
  
  // Filter templates based on search
  const filteredTemplates = templateEntries.filter(([key, template]) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const serviceLine = (template.service_line || key).toLowerCase();
    return serviceLine.includes(query) || 
           (template.additional_sections || []).some((s: any) => s.name?.toLowerCase().includes(query)) ||
           (template.additional_questions || []).some((q: any) => q.question?.toLowerCase().includes(query));
  });
  
  const toggleTemplate = (key: string) => {
    const newExpanded = new Set(expandedTemplates);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedTemplates(newExpanded);
  };
  
  return (
    <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
      {/* Header with Summary Stats */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-3">
          <h5 className="text-lg font-semibold flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            Workplan Service Line Templates
          </h5>
          <div className="flex items-center gap-4 text-sm text-theme-text-secondary">
            <div className="flex items-center gap-1">
              <List className="h-4 w-4" />
              <span className="font-medium">{totalTemplates}</span> templates
            </div>
            <div className="flex items-center gap-1">
              <FileText className="h-4 w-4" />
              <span className="font-medium">{totalSections}</span> sections
            </div>
            <div className="flex items-center gap-1">
              <HelpCircle className="h-4 w-4" />
              <span className="font-medium">{totalQuestions}</span> questions
            </div>
          </div>
        </div>
        
        {/* Search and Filter */}
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-theme-text-secondary" />
            <Input
              placeholder="Search templates, sections, or questions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          {selectedServiceLine && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedServiceLine(null)}
            >
              <X className="h-4 w-4 mr-1" />
              Clear Filter
            </Button>
          )}
        </div>
      </div>
      
      {/* Templates Grid */}
      <div className="space-y-4">
        {filteredTemplates.map(([key, template]) => {
          const sections = template.additional_sections || [];
          const questions = template.additional_questions || [];
          const metadata = template.metadata || {};
          const workplanCount = metadata.workplan_count || 0;
          const confidence = metadata.confidence || 0;
          const extractedAt = metadata.extracted_at || metadata.extractedAt;
          const serviceLineName = template.service_line || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          const isExpanded = expandedTemplates.has(key);
          
          // Group questions by section for better display
          const questionsBySection = questions.reduce((acc: Record<string, any[]>, q: any) => {
            const section = q.section || 'General';
            if (!acc[section]) acc[section] = [];
            acc[section].push(q);
            return acc;
          }, {});
          
          return (
            <Card 
              key={key} 
              className="overflow-hidden border-2 border-blue-200 dark:border-blue-800 hover:border-blue-300 dark:hover:border-blue-700 transition-colors"
            >
              {/* Template Header - Always Visible */}
              <div 
                className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 cursor-pointer"
                onClick={() => toggleTemplate(key)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h6 className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                        {serviceLineName}
                      </h6>
                      {workplanCount > 0 && (
                        <Badge variant="default" className="bg-blue-600 text-white">
                          <BarChart3 className="h-3 w-3 mr-1" />
                          {workplanCount.toLocaleString()} workplans
                        </Badge>
                      )}
                      {confidence > 0 && (
                        <Badge variant="outline" className="border-blue-400 text-blue-700 dark:text-blue-300">
                          {Math.round(confidence * 100)}% confidence
                        </Badge>
                      )}
                      {extractedAt && (
                        <Badge variant="outline" className="text-xs">
                          <Calendar className="h-3 w-3 mr-1" />
                          {new Date(extractedAt).toLocaleDateString()}
                        </Badge>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 text-sm text-blue-700 dark:text-blue-300">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        <span><strong>{sections.length}</strong> sections</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <HelpCircle className="h-4 w-4" />
                        <span><strong>{questions.length}</strong> questions</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4" />
                        <span>Avg <strong>{sections.length > 0 ? Math.round(questions.length / sections.length) : 0}</strong> per section</span>
                      </div>
                    </div>
                  </div>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleTemplate(key);
                    }}
                    className="ml-4"
                  >
                    {isExpanded ? (
                      <>
                        <EyeOff className="h-4 w-4 mr-1" />
                        Collapse
                      </>
                    ) : (
                      <>
                        <Eye className="h-4 w-4 mr-1" />
                        Expand
                      </>
                    )}
                  </Button>
                </div>
              </div>
              
              {/* Expanded Content */}
              {isExpanded && (
                <div className="p-6 bg-white dark:bg-theme-paper space-y-6">
                  {/* Sections Overview */}
                  <div>
                    <h7 className="text-sm font-semibold mb-3 flex items-center gap-2 text-theme-text">
                      <List className="h-4 w-4" />
                      Sections ({sections.length})
                    </h7>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {sections.map((section: any, idx: number) => {
                        const sectionQuestions = questionsBySection[section.name] || [];
                        return (
                          <Card key={idx} className="p-3 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
                            <div className="flex items-start justify-between mb-1">
                              <span className="text-xs font-medium text-theme-text flex-1">
                                {section.name}
                              </span>
                              {section.required && (
                                <Badge variant="outline" className="text-xs ml-1">Required</Badge>
                              )}
                            </div>
                            {sectionQuestions.length > 0 && (
                              <div className="text-xs text-theme-text-secondary mt-1">
                                {sectionQuestions.length} question{sectionQuestions.length !== 1 ? 's' : ''}
                              </div>
                            )}
                            {section.description && (
                              <div className="text-xs text-theme-text-secondary mt-1 italic">
                                {section.description}
                              </div>
                            )}
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Questions by Section */}
                  <div>
                    <h7 className="text-sm font-semibold mb-3 flex items-center gap-2 text-theme-text">
                      <HelpCircle className="h-4 w-4" />
                      Questions by Section ({questions.length} total)
                    </h7>
                    <div className="space-y-4">
                      {Object.entries(questionsBySection).map(([sectionName, sectionQuestions]) => (
                        <Card key={sectionName} className="p-4 bg-blue-50/50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800">
                          <h8 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-3 flex items-center gap-2">
                            <FileText className="h-4 w-4" />
                            {sectionName} ({sectionQuestions.length} questions)
                          </h8>
                          <div className="space-y-3">
                            {sectionQuestions.map((q: any, qIdx: number) => (
                              <div 
                                key={qIdx} 
                                className="p-3 bg-white dark:bg-gray-800 rounded-lg border-l-4 border-blue-500 shadow-sm"
                              >
                                <div className="text-sm text-theme-text leading-relaxed">
                                  {q.question || 'No question text'}
                                </div>
                              </div>
                            ))}
                          </div>
                        </Card>
                      ))}
                    </div>
                  </div>
                  
                  {/* Metadata */}
                  {Object.keys(metadata).length > 0 && (
                    <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                      <h7 className="text-xs font-semibold text-theme-text-secondary mb-2">Metadata</h7>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                        {extractedAt && (
                          <div>
                            <span className="text-theme-text-secondary">Extracted:</span>{' '}
                            <span className="font-medium">{new Date(extractedAt).toLocaleString()}</span>
                          </div>
                        )}
                        {workplanCount > 0 && (
                          <div>
                            <span className="text-theme-text-secondary">Workplans:</span>{' '}
                            <span className="font-medium">{workplanCount.toLocaleString()}</span>
                          </div>
                        )}
                        {confidence > 0 && (
                          <div>
                            <span className="text-theme-text-secondary">Confidence:</span>{' '}
                            <span className="font-medium">{Math.round(confidence * 100)}%</span>
                          </div>
                        )}
                        {metadata.evidence_count !== undefined && (
                          <div>
                            <span className="text-theme-text-secondary">Evidence:</span>{' '}
                            <span className="font-medium">{metadata.evidence_count}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </Card>
          );
        })}
      </div>
      
      {filteredTemplates.length === 0 && (
        <Card className="p-8 text-center">
          <Search className="h-12 w-12 mx-auto mb-4 text-theme-text-secondary" />
          <p className="text-theme-text-secondary">No templates found matching "{searchQuery}"</p>
        </Card>
      )}
    </div>
  );
}

// Export Configuration Section
function ExportConfigurationSection({ formData, updateField, updateArrayField, addArrayItem, removeArrayItem, validateField, validationErrors, editable }: any) {
  const ec = formData.export_configuration || {};
  
  return (
    <div className="space-y-4">
      <Select
        label="Default Format"
        value={ec.default_format || 'DOCX'}
        onChange={(e) => updateField(['export_configuration', 'default_format'], e.target.value)}
        options={[
          { value: 'DOCX', label: 'DOCX' },
          { value: 'PDF', label: 'PDF' },
          { value: 'JSON', label: 'JSON' },
          { value: 'CSV', label: 'CSV' },
        ]}
        disabled={!editable}
      />
      <Input
        label="Coordinate Precision"
        type="number"
        value={ec.coordinate_precision || 2}
        onChange={(e) => updateField(['export_configuration', 'coordinate_precision'], parseInt(e.target.value))}
        helperText="Decimal places for coordinates"
        disabled={!editable}
      />
      <Select
        label="Date Format"
        value={ec.date_format || 'ISO8601'}
        onChange={(e) => updateField(['export_configuration', 'date_format'], e.target.value)}
        options={[
          { value: 'ISO8601', label: 'ISO8601' },
          { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
          { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
          { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
        ]}
        disabled={!editable}
      />
      <Select
        label="Null Value Handling"
        value={ec.null_value_handling || 'null'}
        onChange={(e) => updateField(['export_configuration', 'null_value_handling'], e.target.value)}
        options={[
          { value: 'null', label: 'null' },
          { value: 'empty', label: 'Empty String' },
          { value: 'omit', label: 'Omit Field' },
        ]}
        disabled={!editable}
      />
      <div>
        <label className="block text-sm font-medium text-theme-text mb-2">Required Metadata</label>
        <ArrayFieldEditor
          label=""
          items={ec.required_metadata || []}
          onAdd={() => {
            const items = [...(ec.required_metadata || []), ''];
            updateField(['export_configuration', 'required_metadata'], items);
          }}
          onRemove={(idx) => {
            const items = [...(ec.required_metadata || [])];
            items.splice(idx, 1);
            updateField(['export_configuration', 'required_metadata'], items);
          }}
          onUpdate={(idx, value) => {
            const items = [...(ec.required_metadata || [])];
            items[idx] = value;
            updateField(['export_configuration', 'required_metadata'], items);
          }}
          editable={editable}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-theme-text mb-2">Optional Outputs</label>
        <ArrayFieldEditor
          label=""
          items={ec.optional_outputs || []}
          onAdd={() => {
            const items = [...(ec.optional_outputs || []), ''];
            updateField(['export_configuration', 'optional_outputs'], items);
          }}
          onRemove={(idx) => {
            const items = [...(ec.optional_outputs || [])];
            items.splice(idx, 1);
            updateField(['export_configuration', 'optional_outputs'], items);
          }}
          onUpdate={(idx, value) => {
            const items = [...(ec.optional_outputs || [])];
            items[idx] = value;
            updateField(['export_configuration', 'optional_outputs'], items);
          }}
          editable={editable}
        />
      </div>
    </div>
  );
}

// Generic Section Editor - Handles any unknown/additional sections gracefully
function GenericSectionEditor({ 
  sectionId, 
  sectionData, 
  formData,
  updateField,
  updateArrayField,
  addArrayItem,
  removeArrayItem,
  validateField,
  validationErrors,
  editable 
}: any) {
  return (
    <div className="space-y-4">
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4">
        <div className="flex items-start gap-2">
          <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-blue-900 dark:text-blue-100 text-sm mb-1">Generic Editor</div>
            <p className="text-xs text-blue-700 dark:text-blue-300">
              This section uses a generic editor because it's not part of the standard policy pack structure. 
              All fields are editable and the structure is preserved.
            </p>
          </div>
        </div>
      </div>
      {!sectionData || typeof sectionData !== 'object' ? (
        <div className="p-4">
          <p className="text-sm text-theme-text-secondary mb-4">
            This section contains: {sectionData === null ? 'null' : typeof sectionData}
          </p>
          {editable && (
            <div>
              <label className="block text-sm font-medium text-theme-text mb-1">Value</label>
              <Input
                value={String(sectionData || '')}
                onChange={(e) => {
                  // Try to preserve type
                  const value = e.target.value;
                  if (value === 'true') updateField([sectionId], true);
                  else if (value === 'false') updateField([sectionId], false);
                  else if (value === 'null' || value === '') updateField([sectionId], null);
                  else if (!isNaN(Number(value)) && value !== '') updateField([sectionId], Number(value));
                  else updateField([sectionId], value);
                }}
                disabled={!editable}
              />
            </div>
          )}
        </div>
      ) : Array.isArray(sectionData) ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-theme-text-secondary">Array with {sectionData.length} items</p>
            {editable && (
              <Button variant="ghost" size="sm" onClick={() => addArrayItem([sectionId], null)}>
                <Plus className="h-4 w-4 mr-1" />
                Add Item
              </Button>
            )}
          </div>
          {sectionData.map((item: any, idx: number) => (
            <Card key={idx} className="p-4">
              <div className="flex items-start justify-between mb-3">
                <h4 className="font-semibold">Item {idx + 1}</h4>
                {editable && (
                  <Button variant="ghost" size="sm" onClick={() => removeArrayItem([sectionId], idx)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
              {typeof item === 'object' && item !== null && !Array.isArray(item) ? (
                <ObjectEditor
                  data={item}
                  path={[sectionId, idx.toString()]}
                  updateField={updateField}
                  editable={editable}
                />
              ) : (
                <Input
                  value={String(item || '')}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === 'true') updateArrayField([sectionId], idx, true);
                    else if (value === 'false') updateArrayField([sectionId], idx, false);
                    else if (value === 'null' || value === '') updateArrayField([sectionId], idx, null);
                    else if (!isNaN(Number(value)) && value !== '') updateArrayField([sectionId], idx, Number(value));
                    else updateArrayField([sectionId], idx, value);
                  }}
                  disabled={!editable}
                />
              )}
            </Card>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-theme-text-secondary mb-4">
            Generic editor for section with {Object.keys(sectionData).length} fields
          </p>
          <ObjectEditor
            data={sectionData}
            path={[sectionId]}
            updateField={updateField}
            editable={editable}
          />
        </div>
      )}
    </div>
  );
}

// Recursive object editor for nested structures
function ObjectEditor({ data, path, updateField, editable }: any) {
  return (
    <div className="space-y-3">
      {Object.keys(data).map((key) => {
        const value = data[key];
        const fieldPath = [...path, key];
        const fieldKey = fieldPath.join('.');

        if (value === null || value === undefined) {
          return (
            <div key={key}>
              <label className="block text-sm font-medium text-theme-text mb-1">{key}</label>
              <Input
                value=""
                placeholder="null"
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '') updateField(fieldPath, null);
                  else if (val === 'true') updateField(fieldPath, true);
                  else if (val === 'false') updateField(fieldPath, false);
                  else if (!isNaN(Number(val))) updateField(fieldPath, Number(val));
                  else updateField(fieldPath, val);
                }}
                disabled={!editable}
              />
            </div>
          );
        }

        if (typeof value === 'boolean') {
          return (
            <div key={key}>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={value}
                  onChange={(e) => updateField(fieldPath, e.target.checked)}
                  disabled={!editable}
                  className="rounded border-theme-border"
                />
                <span className="text-sm font-medium text-theme-text">{key}</span>
              </label>
            </div>
          );
        }

        if (typeof value === 'number') {
          return (
            <Input
              key={key}
              label={key}
              type="number"
              value={value}
              onChange={(e) => updateField(fieldPath, parseFloat(e.target.value) || 0)}
              disabled={!editable}
            />
          );
        }

        if (typeof value === 'string') {
          // Use textarea for long strings
          if (value.length > 100 || value.includes('\n')) {
            return (
              <div key={key}>
                <label className="block text-sm font-medium text-theme-text mb-1">{key}</label>
                <textarea
                  value={value}
                  onChange={(e) => updateField(fieldPath, e.target.value)}
                  className="w-full rounded-lg border border-theme-border px-3 py-2 bg-theme-paper text-theme-text focus:outline-none focus:ring-2 focus:ring-primary-500"
                  rows={Math.min(10, Math.max(3, value.split('\n').length))}
                  disabled={!editable}
                />
              </div>
            );
          }
          return (
            <Input
              key={key}
              label={key}
              value={value}
              onChange={(e) => updateField(fieldPath, e.target.value)}
              disabled={!editable}
            />
          );
        }

        if (Array.isArray(value)) {
          return (
            <div key={key}>
              <ArrayFieldEditor
                label={key}
                items={value.map((v: any) => typeof v === 'object' ? JSON.stringify(v) : String(v))}
                onAdd={() => {
                  const newItems = [...value, ''];
                  updateField(fieldPath, newItems);
                }}
                onRemove={(idx) => {
                  const newItems = [...value];
                  newItems.splice(idx, 1);
                  updateField(fieldPath, newItems);
                }}
                onUpdate={(idx, val) => {
                  const newItems = [...value];
                  // Try to parse JSON, otherwise use as string
                  try {
                    newItems[idx] = JSON.parse(val);
                  } catch {
                    newItems[idx] = val;
                  }
                  updateField(fieldPath, newItems);
                }}
                editable={editable}
              />
            </div>
          );
        }

        if (typeof value === 'object') {
          return (
            <Card key={key} className="p-4">
              <h4 className="font-semibold mb-3">{key}</h4>
              <ObjectEditor
                data={value}
                path={fieldPath}
                updateField={updateField}
                editable={editable}
              />
            </Card>
          );
        }

        return (
          <div key={key} className="text-sm text-theme-text-secondary">
            <strong>{key}:</strong> {String(value)}
          </div>
        );
      })}
    </div>
  );
}
